import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, AlertCircle, AlertTriangle, Send, User, Bot, ChevronDown, ChevronRight, Brain, X, FileText, Copy, Download, Code, GitCompare, Eye, CheckCircle, CheckCircle2, Circle, Cloud, Server, Loader2, Menu, ExternalLink, Shield, Network, Search, Wrench, TrendingDown, Activity, ShipWheel } from 'lucide-react';
import OCILogo from '@/assets/icons/oci.png';
import awsLogoDark from '@/assets/icons/aws-dark.svg';
import awsLogoLight from '@/assets/icons/aws.svg';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { ModeToggle } from '@/components/mode-toggle';
import { ChatHistorySidebar } from '@/components/ChatHistorySidebar';
import { webSocketClient, sendChatMessage, handleStreamingResponse, cancelChatMessage, YAMLFix, CloudFix, CodeFix, ThinkingStep } from '@/api/websocket';
import { useAuth } from '@/hooks/useAuth';
import { useChatHistory } from '@/hooks/useChatHistory';
import { useSidebar } from '@/hooks/useSidebar';
import { MobileMenuButton } from '@/components/MobileMenuButton';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { apiClient, type Cluster, type AWSConnection, type OCIConnection, type InventoryNamespace, type InventoryWorkload } from '@/api/client';
import ReactMarkdown from 'react-markdown';
import { getLanguageBadge, DiffView } from './shared/codeDiffUtils';
import remarkGfm from 'remark-gfm';
import { DatadogMetricsChart } from '@/components/DatadogMetricsChart';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: number;
  isStreaming?: boolean;
  thinking?: {
    text: string;
    steps: ThinkingStep[];  // Structured steps from router
    isExpanded: boolean;
  };
  yamlFixes?: Record<string, YAMLFix[]>;
  cloudFixes?: Record<string, CloudFix[]>;
  datadogMetrics?: DatadogMetricResult[];
  codeFixes?: Record<string, CodeFix[]>;
  codeProvider?: string;
}

interface DatadogMetricResult {
  query: string;
  series: Array<{
    metric?: string;
    display_name?: string;
    scope?: string;
    tag_set?: string[];
    pointlist?: [number, number | null][];
    unit?: [{ name?: string; scale_factor?: number }];
    expression?: string;
  }>;
}

interface PromptCategory {
  title: string;
  description: string;
  icon: React.ElementType;
  prompts: string[];
}

interface WorkloadWithNamespace extends InventoryWorkload {
  namespaceName: string;
}

interface InventoryContext {
  namespaces: InventoryNamespace[];
  workloads: WorkloadWithNamespace[];
}

const getPromptCategories = (
  clusters: Cluster[],
  awsConnections: AWSConnection[],
  ociConnections: OCIConnection[] = [],
  inventory?: InventoryContext,
  flowInventory?: InventoryContext,
  ddInventory?: InventoryContext,
): PromptCategory[] => {
  const connectedClusters = clusters.filter(c => c.is_connected);
  const firstCluster = connectedClusters[0]?.cluster_name || 'minikube';
  const secondCluster = connectedClusters.length > 1 ? connectedClusters[1].cluster_name : firstCluster;
  const hasAwsConnections = awsConnections.some(c => c.connection_status === 'connected');
  const hasOciConnections = ociConnections.some(c => c.connection_status === 'connected');

  // Inventory data is pre-shuffled at fetch time for variety on each page load
  const namespaces = inventory?.namespaces || [];
  const ns1 = namespaces[0]?.name;
  const ns2 = namespaces[1]?.name || ns1;
  const ns3 = namespaces[2]?.name || namespaces[1]?.name;

  // Workloads are also pre-shuffled — pick different kinds for variety.
  // Each workload carries its namespaceName so queries always pair the correct workload + namespace.
  const allWorkloads = inventory?.workloads || [];
  const deploy1Obj = allWorkloads.find(w => w.kind === 'Deployment');
  const sampleDeploy1 = deploy1Obj?.name;
  const sampleDeploy1Ns = deploy1Obj?.namespaceName;
  const deploy2Obj = allWorkloads.find(w => w.kind === 'Deployment' && w.name !== sampleDeploy1);
  const sampleDeploy2 = deploy2Obj?.name;
  const nonDeployObj = allWorkloads.find(w => w.kind === 'StatefulSet' || w.kind === 'DaemonSet');
  const sampleWorkload = nonDeployObj?.name || allWorkloads.find(w => w.name !== sampleDeploy1)?.name;
  const sampleWorkloadNs = nonDeployObj?.namespaceName || allWorkloads.find(w => w.name !== sampleDeploy1)?.namespaceName;

  // Flow-active cluster and its inventory (for Network & Traffic prompts)
  const firstFlowCluster = clusters.find(c => c.is_connected && c.has_active_flows)?.cluster_name;

  // Datadog-enabled cluster and its inventory (for Metrics & Observability prompts)
  const ddCluster = connectedClusters.find(c => c.has_datadog)?.cluster_name;
  const ddWorkloads = ddInventory?.workloads || [];
  const ddDeploy1Obj = ddWorkloads.find(w => w.kind === 'Deployment');
  const ddDeploy1 = ddDeploy1Obj?.name;
  const ddDeploy1Ns = ddDeploy1Obj?.namespaceName;
  const ddWorkload2Obj = ddWorkloads.find(w => w.name !== ddDeploy1 && (w.kind === 'StatefulSet' || w.kind === 'DaemonSet' || w.kind === 'Deployment'));
  const ddWorkload2 = ddWorkload2Obj?.name;
  const ddWorkload2Ns = ddWorkload2Obj?.namespaceName;
  const flowNs = flowInventory?.namespaces || [];
  const flowNs1 = flowNs[0]?.name;
  const flowNs2 = flowNs[1]?.name || flowNs1;

  const categories: PromptCategory[] = [
    {
      title: 'Troubleshooting',
      description: 'Diagnose crashes, errors & incidents',
      icon: Search,
      prompts: [
        `Are any pods crashing or restarting on @${firstCluster}?`,
        ns1
          ? `Show me recent warning events in the ${ns1} namespace on @${firstCluster}`
          : `Show me recent warning events across all namespaces on @${firstCluster}`,
        `Which deployments have pods that aren't ready on @${firstCluster}?`,
      ],
    },
    {
      title: 'Configuration',
      description: 'Review & modify cluster resources',
      icon: Wrench,
      prompts: [
        sampleDeploy1
          ? `Add resource limits to the ${sampleDeploy1} deployment on @${firstCluster}`
          : `Which deployments are missing resource limits on @${firstCluster}?`,
        ns2
          ? `Create a network policy to isolate the ${ns2} namespace on @${firstCluster}`
          : `Create a network policy to isolate the default namespace on @${firstCluster}`,
        sampleWorkload
          ? `Set up a security context to run ${sampleWorkload} as non-root on @${firstCluster}`
          : `Which workloads are running containers as root on @${firstCluster}?`,
      ],
    },
    // Network & Traffic — only shown when at least one cluster has active flows
    ...(firstFlowCluster ? [{
      title: 'Network & Traffic',
      description: 'Flows, top talkers & connections',
      icon: Network,
      prompts: [
        `What does network traffic look like on @${firstFlowCluster}? Show top talkers`,
        flowNs2
          ? `Show me all traffic flowing in and out of the ${flowNs2} namespace on @${firstFlowCluster}`
          : `Show me all traffic flowing in and out of the default namespace on @${firstFlowCluster}`,
        flowNs1
          ? `Which services in ${flowNs1} are receiving the most traffic on @${firstFlowCluster}?`
          : `Which services are receiving the most traffic on @${firstFlowCluster}?`,
      ],
    }] : []),
    // Metrics & Observability — only shown when at least one cluster has Datadog
    ...(ddCluster ? [{
      title: 'Metrics & Observability',
      description: 'CPU, memory, network & historical trends',
      icon: Activity,
      prompts: [
        ddDeploy1
          ? `Show me the CPU and memory utilization of ${ddDeploy1} in ${ddDeploy1Ns} on @${ddCluster} over the last 24 hours`
          : `Show me the CPU and memory utilization of all workloads on @${ddCluster} over the last 24 hours`,
        ddWorkload2
          ? `What does the network traffic (bytes sent and received) look like for ${ddWorkload2} in ${ddWorkload2Ns} on @${ddCluster} over the last 12 hours?`
          : `What does the network traffic look like across workloads on @${ddCluster} over the last 12 hours?`,
        `Are there any disk pressure or storage capacity issues on @${ddCluster}? Show me volume usage metrics for the last 6 hours`,
      ],
    }] : []),
    {
      title: 'Cluster Overview',
      description: 'Explore your cluster infrastructure & workloads',
      icon: ShipWheel,
      prompts: [
        `Give me a full overview of workloads running on @${secondCluster}`,
        `Which namespaces are using the most resources on @${firstCluster}?`,
        ns3
          ? `Show me all services and their endpoints in ${ns3} on @${secondCluster}`
          : `Show me all services and their endpoints on @${firstCluster}`,
      ],
    },
    {
      title: 'Cost Optimization',
      description: 'Right-size resources & reduce waste',
      icon: TrendingDown,
      prompts: [
        `Which deployments on @${secondCluster} are missing resource requests or limits?`,
        sampleDeploy1
          ? `How many replicas does ${sampleDeploy1} have and could it be scaled down on @${firstCluster}?`
          : `Which deployments on @${firstCluster} have more replicas than they need?`,
        ns1
          ? `Show me resource requests vs limits for all pods in ${ns1} on @${secondCluster}`
          : `Show me resource requests vs limits for all pods on @${firstCluster}`,
      ],
    },
    {
      title: 'Security & Compliance',
      description: 'Findings, attack chains & vulnerabilities',
      icon: Shield,
      prompts: [
        `What are the most critical security findings on @${firstCluster}?`,
        `Are there any attack chains that could be exploited on @${firstCluster}?`,
        ...(hasAwsConnections ? [`Show me high severity findings across my AWS environment on @cloud:aws`] :
            hasOciConnections ? [`Show me high severity findings across my OCI environment on @cloud:oci`] :
            [`Show me all open security findings and their severity on @${firstCluster}`]),
      ],
    },
  ];

  return categories;
};

// Function to parse thinking text into discrete steps (fallback when router doesn't provide structured steps)
const parseThinkingStepsFallback = (text: string): ThinkingStep[] => {
  if (!text) return [];

  const steps: ThinkingStep[] = [];
  const lines = text.split('\n').filter(line => line.trim());

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return;

    let type = 'general';
    let target: string | undefined;
    let is_cloud = false;

    // Detect step type based on content
    if (trimmedLine.toLowerCase().includes('analyzing') ||
        trimmedLine.toLowerCase().includes('analysis') ||
        trimmedLine.toLowerCase().includes('detected')) {
      type = 'analysis';
    } else if (trimmedLine.toLowerCase().includes('executing') ||
               trimmedLine.toLowerCase().includes('running')) {
      type = 'execution';
    } else if (trimmedLine.toLowerCase().includes('result') ||
               trimmedLine.toLowerCase().includes('response') ||
               trimmedLine.toLowerCase().includes('generating')) {
      type = 'result';
    } else if (trimmedLine.startsWith('@')) {
      type = 'action';
    }

    // Extract target from @mentions
    const mentionMatch = trimmedLine.match(/@(cloud:)?([a-zA-Z0-9_-]+):/);
    if (mentionMatch) {
      target = mentionMatch[2];
      is_cloud = mentionMatch[1] === 'cloud:' ||
                target === 'aws' ||
                target.startsWith('aws-connection');
      type = 'action';
    }

    steps.push({
      id: `step-${index}`,
      text: trimmedLine,
      type,
      status: 'complete',
      target,
      is_cloud,
    });
  });

  return steps;
};

// Component for a single thinking step checkbox item
interface ThinkingStepItemProps {
  step: ThinkingStep;
  isComplete: boolean;
  isActive: boolean;
  clusters: Cluster[];
  awsConnections: AWSConnection[];
}

function ThinkingStepItem({ step, isComplete, isActive, clusters, awsConnections }: ThinkingStepItemProps) {
  const getStepIcon = () => {
    if (isActive && !isComplete) {
      return <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />;
    }
    if (isComplete) {
      return <CheckCircle2 className="h-4 w-4 text-muted-foreground" />;
    }
    return <Circle className="h-4 w-4 text-slate-300 dark:text-slate-600" />;
  };

  // Render text with styled mentions
  const renderStepText = (text: string) => {
    const mentionRegex = /@(cloud:)?([a-zA-Z0-9_-]+):/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      let mentionStart = match.index;
      let mentionEnd = match.index + match[0].length;
      let hasCloudPrefix = match[1] === 'cloud:';
      let targetName = match[2];
      
      // Handle malformed "@cloud oci-connection:" pattern (space instead of colon after @cloud)
      if (!hasCloudPrefix && targetName.toLowerCase() === 'cloud') {
        const textAfter = text.substring(mentionEnd);
        const spaceOciMatch = textAfter.match(/^ (oci-[a-zA-Z0-9_-]+):/);
        if (spaceOciMatch) {
          hasCloudPrefix = true;
          targetName = spaceOciMatch[1];
          mentionEnd += spaceOciMatch[0].length;
        }
      }
      
      const isOciConnection = targetName === 'oci' || targetName.toLowerCase().startsWith('oci-');
      const isCloudMention = hasCloudPrefix ||
                            targetName === 'aws' ||
                            targetName.startsWith('aws-connection') ||
                            isOciConnection;

      if (mentionStart > lastIndex) {
        parts.push(<span key={`text-${lastIndex}`}>{text.substring(lastIndex, mentionStart)}</span>);
      }

      if (isCloudMention) {
        const connection = awsConnections?.find(c => c.account_id === targetName);
        parts.push(
          <span
            key={`mention-${mentionStart}`}
            className={cn(
              "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium",
              isOciConnection
                ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"
                : connection?.connection_status === 'connected'
                  ? "bg-muted text-foreground"
                  : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
            )}
          >
            <Cloud className="h-3 w-3" />
            {targetName}
          </span>
        );
      } else {
        const cluster = clusters.find(c => c.cluster_name === targetName);
        parts.push(
          <span
            key={`mention-${mentionStart}`}
            className={cn(
              "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium",
              "bg-zinc-100 text-zinc-900 dark:bg-zinc-900 dark:text-zinc-100"
            )}
          >
            <Server className="h-3 w-3" />
            {targetName}
          </span>
        );
      }

      // Skip past the mention
      lastIndex = mentionEnd;
    }

    if (lastIndex < text.length) {
      parts.push(<span key={`text-end`}>{text.substring(lastIndex)}</span>);
    }

    return parts.length > 0 ? <>{parts}</> : text;
  };

  return (
    <div
      className={cn(
        "flex items-start gap-3 py-2 px-3 rounded-lg border-l-2 border-l-border transition-all duration-300",
        "animate-in slide-in-from-left-2 fade-in duration-300",
        isActive && !isComplete ? "bg-muted/50 " : "",
        isComplete ? "opacity-80" : ""
      )}
    >
      <div className="flex-shrink-0 mt-0.5">
        {getStepIcon()}
      </div>
      <div className={cn(
        "flex-1 text-sm leading-relaxed",
        isComplete ? "text-slate-600 dark:text-slate-400" : "text-slate-800 dark:text-slate-200"
      )}>
        {renderStepText(step.text)}
      </div>
    </div>
  );
}

// Component for displaying thinking data in completed messages
interface ThinkingDisplayProps {
  thinking: {
    text: string;
    steps: ThinkingStep[];  // Structured steps from router
    isExpanded: boolean;
  };
  messageId: string;
  clusters: Cluster[]; // Pass clusters for styling
  awsConnections: AWSConnection[]; // Pass AWS connections for cloud mention styling
  isStreaming?: boolean; // Whether thinking is still being streamed
}

function ThinkingDisplay({ thinking, messageId, clusters, awsConnections, isStreaming = false }: ThinkingDisplayProps) {
  // Default to expanded when streaming so users can see progress in real-time
  // For completed messages, use the stored isExpanded state (defaults to false)
  const [isExpanded, setIsExpanded] = React.useState(isStreaming || thinking.isExpanded);
  const stepsContainerRef = React.useRef<HTMLDivElement>(null);

  // Use provided structured steps, or fall back to parsing text if steps are empty
  const steps = thinking.steps && thinking.steps.length > 0
    ? thinking.steps
    : parseThinkingStepsFallback(thinking.text);
  const completedSteps = steps.length;

  // Auto-scroll to bottom when new steps arrive during streaming
  React.useEffect(() => {
    if (isStreaming && isExpanded && stepsContainerRef.current) {
      stepsContainerRef.current.scrollTo({
        top: stepsContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [steps.length, isStreaming, isExpanded]);

  return (
    <div className="max-w-[80%]">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "group flex items-center gap-3 px-4 py-3 bg-muted/50",
          "hover:bg-muted/80",
          "rounded-xl border border-border/50",
          "transition-all duration-300 text-sm font-medium shadow-lg hover:shadow-xl",
          "focus:outline-none focus:ring-2 focus:ring-ring/30 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        )}
      >
        {isExpanded ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground dark:text-muted-foreground transition-transform duration-300" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground dark:text-muted-foreground group-hover:translate-x-0.5 transition-transform duration-300" />
        )}

        <span className="text-foreground dark:text-muted-foreground font-medium whitespace-nowrap">
          {isStreaming ? "Thinking..." : "Thought about this"}
        </span>

        {/* Step counter badge */}
        {completedSteps > 0 && (
          <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-zinc-100 text-zinc-900 dark:bg-zinc-900 dark:text-zinc-100 whitespace-nowrap">
            {completedSteps} {completedSteps === 1 ? 'step' : 'steps'}
          </span>
        )}

        {isStreaming ? (
          <div className="flex items-center space-x-1.5 ml-2">
            <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{animationDelay: '-0.3s'}}></div>
            <div className="w-1.5 h-1.5 bg-muted-foreground/70 rounded-full animate-bounce" style={{animationDelay: '-0.15s'}}></div>
            <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce"></div>
          </div>
        ) : (
          <div className="ml-auto flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-foreground" />
          </div>
        )}
      </button>

      {isExpanded && steps.length > 0 && (
        <div
          ref={stepsContainerRef}
          className="mt-3 p-3 bg-gradient-to-br from-neutral-50/90 to-neutral-100/90 dark:from-neutral-900/90 dark:to-neutral-800/90 border border-neutral-200/50 dark:border-neutral-700/50 rounded-xl max-h-64 overflow-y-auto backdrop-blur-sm shadow-inner"
        >
          <div className="space-y-1">
            {steps.map((step, index) => (
              <ThinkingStepItem
                key={step.id}
                step={step}
                isComplete={!isStreaming || index < steps.length - 1}
                isActive={isStreaming && index === steps.length - 1}
                clusters={clusters}
                awsConnections={awsConnections}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// Component for displaying YAML fixes
interface YAMLFixesDisplayProps {
  yamlFixes: Record<string, YAMLFix[]>;
  clusters: Cluster[];
  onStoreChange?: (clusterName: string, fix: YAMLFix) => void;
  yamlChangeStatus?: {[key: string]: 'success' | 'error'};
}

function YAMLFixesDisplay({ yamlFixes, clusters, onStoreChange, yamlChangeStatus }: YAMLFixesDisplayProps) {
  const [expandedFixes, setExpandedFixes] = useState<Set<string>>(new Set());
  const [showDiffView, setShowDiffView] = useState<Set<string>>(new Set());
  const [copiedFix, setCopiedFix] = useState<string | null>(null);

  const toggleFixExpansion = (fixKey: string) => {
    setExpandedFixes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fixKey)) {
        newSet.delete(fixKey);
      } else {
        newSet.add(fixKey);
      }
      return newSet;
    });
  };

  const toggleDiffView = (fixKey: string) => {
    setShowDiffView(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fixKey)) {
        newSet.delete(fixKey);
      } else {
        newSet.add(fixKey);
      }
      return newSet;
    });
  };

  const copyYamlToClipboard = async (yamlContent: string, fixKey: string) => {
    try {
      await navigator.clipboard.writeText(yamlContent);
      setCopiedFix(fixKey);
      setTimeout(() => setCopiedFix(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const downloadYamlFile = (yamlContent: string, filePath: string) => {
    const blob = new Blob([yamlContent], { type: 'text/yaml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const filename = filePath.split('/').pop() || 'fix.yaml';
    link.setAttribute('download', filename);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getFileTypeBadge = (fileType: string) => {
    switch (fileType) {
      case 'create': return <Badge className="bg-muted text-foreground border-border">Create</Badge>;
      case 'update': return <Badge className="bg-muted text-foreground border-border">Update</Badge>;
      case 'delete': return <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">Delete</Badge>;
      default: return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getFileTypeIcon = (fileType: string) => {
    switch (fileType) {
      case 'create': return <FileText className="h-4 w-4 text-muted-foreground" />;
      case 'update': return <FileText className="h-4 w-4 text-muted-foreground" />;
      case 'delete': return <FileText className="h-4 w-4 text-red-600" />;
      default: return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-px h-6 bg-border"></div>
        <h4 className="text-sm font-semibold text-foreground">Configuration Fixes</h4>
        <Badge variant="outline" className="text-xs">
          {Object.values(yamlFixes).reduce((total, fixes) => total + fixes.length, 0)} {Object.values(yamlFixes).reduce((total, fixes) => total + fixes.length, 0) === 1 ? 'fix' : 'fixes'}
        </Badge>
      </div>

      {Object.entries(yamlFixes).map(([clusterName, fixes]) => {
        const cluster = clusters.find(c => c.cluster_name === clusterName);
        
        return (
          <div key={clusterName} className="space-y-3">
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-2 h-2 rounded-full",
                cluster?.is_connected 
                  ? "bg-emerald-500" 
                  : "bg-red-500"
              )} />
              <span className="text-sm font-medium text-muted-foreground">
                {clusterName} ({fixes.length} {fixes.length === 1 ? 'fix' : 'fixes'})
              </span>
            </div>

            {fixes.map((fix, index) => {
              const fixKey = `${clusterName}-${index}`;
              const statusKey = `${clusterName}-${fix.file_path}`;
              const isExpanded = expandedFixes.has(fixKey);
              const isCopied = copiedFix === fixKey;
              const changeStatus = yamlChangeStatus?.[statusKey];

              return (
                <Card key={fixKey} className="border-l-2 border-l-border overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="flex-shrink-0">
                          {getFileTypeIcon(fix.file_type)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate" title={fix.file_path}>{fix.file_path}</div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {getFileTypeBadge(fix.file_type)}
                            {fix.resource_name && (
                              <span className="text-xs text-muted-foreground truncate">
                                {fix.resource_kind}/{fix.resource_name}
                                {fix.resource_namespace && ` in ${fix.resource_namespace}`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyYamlToClipboard(fix.yaml_content, fixKey)}
                          className="text-xs"
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          {isCopied ? 'Copied!' : 'Copy'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadYamlFile(fix.yaml_content, fix.file_path)}
                          className="text-xs"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => onStoreChange?.(clusterName, fix)}
                          disabled={changeStatus === 'success'}
                          className={`text-xs ${
                            changeStatus === 'success' 
                              ? 'bg-foreground hover:bg-foreground cursor-default text-background' 
                              : changeStatus === 'error'
                              ? 'bg-red-600 hover:bg-red-700'
                              : 'bg-foreground hover:bg-foreground/90 text-background'
                          }`}
                        >
                          {changeStatus === 'success' ? (
                            <>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Stored
                            </>
                          ) : changeStatus === 'error' ? (
                            <>
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Failed
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Store Change
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {fix.explanation && (
                      <div>
                        <h5 className="text-xs font-medium text-foreground mb-2">What this fixes:</h5>
                        <p className="text-xs text-muted-foreground">{fix.explanation}</p>
                      </div>
                    )}

                    <Collapsible open={isExpanded} onOpenChange={() => toggleFixExpansion(fixKey)}>
                      <CollapsibleTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full justify-between text-xs">
                          <span className="flex items-center gap-2">
                            <Code className="h-3 w-3" />
                            View YAML Configuration
                          </span>
                          {isExpanded ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ChevronRight className="h-3 w-3" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-3">
                        <div className="space-y-3">
                          {/* Toggle between single view and diff view for updates */}
                          {fix.existing_yaml && fix.file_type === 'update' && (
                            <div className="flex justify-center">
                              <div className="flex items-center gap-2 bg-muted p-1 rounded-md">
                                <Button
                                  variant={!showDiffView.has(fixKey) ? "default" : "ghost"}
                                  size="sm"
                                  onClick={() => setShowDiffView(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(fixKey);
                                    return newSet;
                                  })}
                                  className="text-xs h-6"
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  New YAML
                                </Button>
                                <Button
                                  variant={showDiffView.has(fixKey) ? "default" : "ghost"}
                                  size="sm"
                                  onClick={() => toggleDiffView(fixKey)}
                                  className="text-xs h-6"
                                >
                                  <GitCompare className="h-3 w-3 mr-1" />
                                  Show Diff
                                </Button>
                              </div>
                            </div>
                          )}
                          
                          {/* YAML Content Display */}
                          {fix.existing_yaml && fix.file_type === 'update' && showDiffView.has(fixKey) ? (
                            // Unified diff view for updates
                            <div className="bg-muted p-3 rounded-md border font-mono text-xs overflow-x-auto max-h-64">
                              <DiffView oldText={fix.existing_yaml} newText={fix.yaml_content} gutterWidth="w-6" />
                            </div>
                          ) : (
                            // Single view (for creates, deletes, or when not in diff mode)
                            <pre className={cn(
                              "p-3 rounded-md border font-mono text-xs overflow-x-auto max-h-64",
                              fix.file_type === 'delete' 
                                ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                                : fix.file_type === 'create'
                                ? "bg-muted/50 border-border"
                                : "bg-muted"
                            )}>
                              <code className="text-foreground">
                                {fix.file_type === 'delete' && fix.existing_yaml 
                                  ? fix.existing_yaml 
                                  : fix.yaml_content}
                              </code>
                            </pre>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// Component for displaying Cloud fixes (Terraform, CloudFormation, AWS CLI, OCI CLI)
interface CloudFixesDisplayProps {
  cloudFixes: Record<string, CloudFix[]>;
  awsConnections: AWSConnection[];
  ociConnections?: OCIConnection[];
  onStoreChange?: (connectionName: string, fix: CloudFix) => void;
  cloudChangeStatus?: {[key: string]: 'success' | 'error'};
}

function CloudFixesDisplay({ cloudFixes, awsConnections, ociConnections = [], onStoreChange, cloudChangeStatus }: CloudFixesDisplayProps) {
  const [expandedFixes, setExpandedFixes] = useState<Set<string>>(new Set());
  const [showDiffView, setShowDiffView] = useState<Set<string>>(new Set());
  const [copiedFix, setCopiedFix] = useState<string | null>(null);

  const toggleFixExpansion = (fixKey: string) => {
    setExpandedFixes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fixKey)) {
        newSet.delete(fixKey);
      } else {
        newSet.add(fixKey);
      }
      return newSet;
    });
  };

  const toggleDiffView = (fixKey: string) => {
    setShowDiffView(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fixKey)) {
        newSet.delete(fixKey);
      } else {
        newSet.add(fixKey);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (content: string, fixKey: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedFix(fixKey);
      setTimeout(() => setCopiedFix(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const downloadFile = (content: string, filePath: string, fixType: string) => {
    const extension = fixType === 'terraform' ? 'tf' : fixType === 'cloudformation' ? 'yaml' : fixType === 'pulumi' ? 'ts' : 'sh';
    const mimeType = fixType === 'terraform' ? 'text/plain' : fixType === 'cloudformation' ? 'text/yaml' : fixType === 'pulumi' ? 'text/typescript' : 'text/x-sh';
    const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const filename = filePath || `fix.${extension}`;
    link.setAttribute('download', filename.split('/').pop() || `fix.${extension}`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getFixTypeBadge = (fixType: string) => {
    switch (fixType) {
      case 'terraform':
        return <Badge className="bg-muted text-foreground border-border">Terraform</Badge>;
      case 'cloudformation':
        return <Badge className="bg-muted text-foreground border-border">CloudFormation</Badge>;
      case 'pulumi':
        return <Badge className="bg-muted text-foreground border-border">Pulumi</Badge>;
      case 'aws_cli':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800">AWS CLI</Badge>;
      case 'oci_cli':
        return <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">OCI CLI</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'create':
        return <Badge className="bg-muted text-foreground border-border">Create</Badge>;
      case 'update':
        return <Badge className="bg-muted text-foreground border-border">Update</Badge>;
      case 'delete':
        return <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">Delete</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getFixTypeIcon = (fixType: string, action: string) => {
    const colorClass = action === 'create' ? 'text-muted-foreground' : action === 'delete' ? 'text-red-600' : 'text-muted-foreground';
    return <Cloud className={cn("h-4 w-4", colorClass)} />;
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-px h-6 bg-border"></div>
        <h4 className="text-sm font-semibold text-foreground">Cloud Fixes</h4>
        <Badge variant="outline" className="text-xs border-border">
          {Object.values(cloudFixes).reduce((total, fixes) => total + fixes.length, 0)} {Object.values(cloudFixes).reduce((total, fixes) => total + fixes.length, 0) === 1 ? 'fix' : 'fixes'}
        </Badge>
      </div>

      {Object.entries(cloudFixes).map(([connectionName, fixes]) => {
        const isOciConnection = connectionName.startsWith('oci-');
        const connection = awsConnections.find(c => c.account_id === connectionName);
        // For OCI connections, find the matching OCI connection (convert hyphens back to spaces for matching)
        const ociConnection = isOciConnection 
          ? ociConnections.find(c => {
              const normalizedConnName = connectionName.replace('oci-', '');
              const normalizedOciName = (c.connection_name || '').replace(/ /g, '-');
              return normalizedConnName === normalizedOciName || normalizedConnName === c.connection_name;
            })
          : undefined;

        return (
          <div key={connectionName} className="space-y-3">
            <div className="flex items-center gap-2">
              <Cloud className={cn(
                "h-4 w-4",
                isOciConnection
                  ? (ociConnection?.connection_status === 'connected' ? "text-foreground" : "text-red-500")
                  : (connection?.connection_status === 'connected' ? "text-foreground" : "text-red-500")
              )} />
              <span className="text-sm font-medium text-muted-foreground">
                {connectionName} ({fixes.length} {fixes.length === 1 ? 'fix' : 'fixes'})
              </span>
            </div>

            {fixes.map((fix, index) => {
              const fixKey = `${connectionName}-${index}`;
              const statusKey = `${connectionName}-${fix.resource_type}-${fix.resource_name}`;
              const isExpanded = expandedFixes.has(fixKey);
              const isCopied = copiedFix === fixKey;
              const changeStatus = cloudChangeStatus?.[statusKey];

              return (
                <Card key={fixKey} className="border-l-2 border-l-border overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="flex-shrink-0">
                          {getFixTypeIcon(fix.fix_type, fix.action)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate" title={fix.resource_name}>{fix.resource_name}</div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {getFixTypeBadge(fix.fix_type)}
                            {getActionBadge(fix.action)}
                            <span className="text-xs text-muted-foreground truncate max-w-[200px]" title={fix.resource_type}>
                              {fix.resource_type}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(fix.content, fixKey)}
                          className="text-xs"
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          {isCopied ? 'Copied!' : 'Copy'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadFile(fix.content, fix.file_path || '', fix.fix_type)}
                          className="text-xs"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        {onStoreChange && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => onStoreChange?.(connectionName, fix)}
                            disabled={changeStatus === 'success'}
                            className={cn(
                              "text-xs",
                              changeStatus === 'success'
                                ? 'bg-foreground hover:bg-foreground cursor-default text-background'
                                : changeStatus === 'error'
                                ? 'bg-red-600 hover:bg-red-700'
                                : 'bg-foreground hover:bg-foreground/90 text-background'
                            )}
                          >
                            {changeStatus === 'success' ? (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Stored
                              </>
                            ) : changeStatus === 'error' ? (
                              <>
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Failed
                              </>
                            ) : (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Store Change
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {fix.explanation && (
                      <div>
                        <h5 className="text-xs font-medium text-foreground mb-2">What this fixes:</h5>
                        <p className="text-xs text-muted-foreground">{fix.explanation}</p>
                      </div>
                    )}

                    <Collapsible open={isExpanded} onOpenChange={() => toggleFixExpansion(fixKey)}>
                      <CollapsibleTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full justify-between text-xs">
                          <span className="flex items-center gap-2">
                            <Code className="h-3 w-3" />
                            View {fix.fix_type === 'terraform' ? 'Terraform' : fix.fix_type === 'cloudformation' ? 'CloudFormation' : fix.fix_type === 'pulumi' ? 'Pulumi' : fix.fix_type === 'oci_cli' ? 'OCI CLI' : 'AWS CLI'} Code
                          </span>
                          {isExpanded ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ChevronRight className="h-3 w-3" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-3">
                        <div className="space-y-3">
                          {/* Toggle between single view and diff view for updates */}
                          {fix.existing_content && fix.action === 'update' && (
                            <div className="flex justify-center">
                              <div className="flex items-center gap-2 bg-muted p-1 rounded-md">
                                <Button
                                  variant={!showDiffView.has(fixKey) ? "default" : "ghost"}
                                  size="sm"
                                  onClick={() => setShowDiffView(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(fixKey);
                                    return newSet;
                                  })}
                                  className="text-xs h-6"
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  New Code
                                </Button>
                                <Button
                                  variant={showDiffView.has(fixKey) ? "default" : "ghost"}
                                  size="sm"
                                  onClick={() => toggleDiffView(fixKey)}
                                  className="text-xs h-6"
                                >
                                  <GitCompare className="h-3 w-3 mr-1" />
                                  Show Diff
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Code Content Display */}
                          {fix.existing_content && fix.action === 'update' && showDiffView.has(fixKey) ? (
                            // Unified diff view for updates
                            <div className="bg-muted p-3 rounded-md border font-mono text-xs overflow-x-auto max-h-64">
                              <DiffView oldText={fix.existing_content} newText={fix.content} gutterWidth="w-6" />
                            </div>
                          ) : (
                            // Single view (for creates, deletes, or when not in diff mode)
                            <pre className={cn(
                              "p-3 rounded-md border font-mono text-xs overflow-x-auto max-h-64",
                              fix.action === 'delete'
                                ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                                : fix.action === 'create'
                                ? "bg-muted/50 border-border"
                                : "bg-muted"
                            )}>
                              <code className="text-foreground">
                                {fix.action === 'delete' && fix.existing_content
                                  ? fix.existing_content
                                  : fix.content}
                              </code>
                            </pre>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}


// Component for displaying source code fixes from connected repos
interface CodeFixesDisplayProps {
  codeFixes: Record<string, CodeFix[]>;
  onStoreChange?: (repoName: string, fixes: CodeFix[]) => void;
  codeChangeStatus?: {[key: string]: 'success' | 'error' | 'loading'};
}

function CodeFixesDisplay({ codeFixes, onStoreChange, codeChangeStatus }: CodeFixesDisplayProps) {
  const [expandedFixes, setExpandedFixes] = useState<Set<string>>(new Set());
  const [copiedFix, setCopiedFix] = useState<string | null>(null);

  const toggleFixExpansion = (fixKey: string) => {
    setExpandedFixes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fixKey)) {
        newSet.delete(fixKey);
      } else {
        newSet.add(fixKey);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (content: string, fixKey: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedFix(fixKey);
      setTimeout(() => setCopiedFix(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };


  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-px h-6 bg-border"></div>
        <h4 className="text-sm font-semibold text-foreground">Code Fixes</h4>
        <Badge variant="outline" className="text-xs border-border">
          {Object.values(codeFixes).reduce((total, fixes) => total + fixes.length, 0)} {Object.values(codeFixes).reduce((total, fixes) => total + fixes.length, 0) === 1 ? 'fix' : 'fixes'}
        </Badge>
      </div>

      {Object.entries(codeFixes).map(([repoName, fixes]) => {
        const repoStatusKey = `repo-${repoName}`;
        const repoStatus = codeChangeStatus?.[repoStatusKey];

        return (
        <div key={repoName} className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Code className="h-4 w-4 text-foreground flex-shrink-0" />
              <span className="text-sm font-medium text-muted-foreground truncate">
                {repoName} ({fixes.length} {fixes.length === 1 ? 'fix' : 'fixes'})
              </span>
            </div>
            {onStoreChange && (
              <Button
                variant="default"
                size="sm"
                onClick={() => onStoreChange?.(repoName, fixes)}
                disabled={repoStatus === 'success' || repoStatus === 'loading'}
                className={cn(
                  "text-xs flex-shrink-0",
                  repoStatus === 'success'
                    ? 'bg-foreground hover:bg-foreground cursor-default text-background'
                    : repoStatus === 'error'
                    ? 'bg-red-600 hover:bg-red-700'
                    : repoStatus === 'loading'
                    ? 'bg-foreground opacity-70 cursor-default text-background'
                    : 'bg-foreground hover:bg-foreground/90 text-background'
                )}
              >
                {repoStatus === 'success' ? (
                  <>
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Stored
                  </>
                ) : repoStatus === 'error' ? (
                  <>
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Failed
                  </>
                ) : repoStatus === 'loading' ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Storing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Store {fixes.length === 1 ? 'Change' : `${fixes.length} Changes`}
                  </>
                )}
              </Button>
            )}
          </div>

          {fixes.map((fix, index) => {
            const fixKey = `${repoName}-${index}`;
            const isExpanded = expandedFixes.has(fixKey);
            const isCopied = copiedFix === fixKey;

            return (
              <Card key={fixKey} className="border-l-2 border-l-border overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="flex-shrink-0">
                        <Code className="h-4 w-4 text-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate" title={fix.file_path}>{fix.file_path}</div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {fix.language && getLanguageBadge(fix.language)}
                          <span className="text-xs text-muted-foreground">
                            Lines {fix.line_range[0]}–{fix.line_range[1]}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(fix.fixed_code, fixKey)}
                        className="text-xs"
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        {isCopied ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {fix.explanation && (
                    <div>
                      <h5 className="text-xs font-medium text-foreground mb-2">What this fixes:</h5>
                      <p className="text-xs text-muted-foreground">{fix.explanation}</p>
                    </div>
                  )}

                  <Collapsible open={isExpanded} onOpenChange={() => toggleFixExpansion(fixKey)}>
                    <CollapsibleTrigger asChild>
                      <Button variant="outline" size="sm" className="w-full justify-between text-xs">
                        <span className="flex items-center gap-2">
                          <GitCompare className="h-3 w-3" />
                          View Diff
                        </span>
                        {isExpanded ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3">
                      <div className="bg-muted p-3 rounded-md border font-mono text-xs overflow-x-auto max-h-64">
                        <DiffView oldText={fix.original_code} newText={fix.fixed_code} gutterWidth="w-6" />
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </CardContent>
              </Card>
            );
          })}
        </div>
        );
      })}
    </div>
  );
}


export function KubernetesChat() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isMobile } = useSidebar();
  const { 
    sessions,
    currentSessionId, 
    setCurrentSessionId,
    loading: chatHistoryLoading,
    deleteSession,
    restoreSession,
    updateSessionTitle,
    loadSession,
    refreshSessions
  } = useChatHistory();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [showConnectionStatus, setShowConnectionStatus] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [hasConnectedBefore, setHasConnectedBefore] = useState(false);
  const [isInitialConnection, setIsInitialConnection] = useState(true);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [currentBotMessage, setCurrentBotMessage] = useState<string>('');
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Thinking state management
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingText, setThinkingText] = useState<string>('');
  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
  const [isThinkingExpanded, setIsThinkingExpanded] = useState(false);
  const [thinkingComplete, setThinkingComplete] = useState(false);
  
  // Cancellation state management
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null);
  const [canCancel, setCanCancel] = useState(false);
  const activeHandlerRef = useRef<((message: any) => void) | null>(null);
  const cancelledRef = useRef(false);
  const messageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist the last user query and message ID for "Store Change" clicks,
  // since input is cleared in handleSend and currentMessageId is cleared in onComplete.
  const lastUserQueryRef = useRef<string>('');
  const lastMessageIdRef = useRef<string>('');
  
  // YAML fixes state management
  const [currentYAMLFixes, setCurrentYAMLFixes] = useState<Record<string, YAMLFix[]> | null>(null);
  const [waitingForFixes, setWaitingForFixes] = useState(false);
  const waitingForFixesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Datadog metrics state (for inline chart rendering)
  const [currentDatadogMetrics, setCurrentDatadogMetrics] = useState<DatadogMetricResult[] | null>(null);

  // Cloud fixes state management
  const [currentCloudFixes, setCurrentCloudFixes] = useState<Record<string, CloudFix[]> | null>(null);

  // YAML change status state
  const [yamlChangeStatus, setYamlChangeStatus] = useState<{[key: string]: 'success' | 'error'}>({});

  // Cloud change status state
  const [cloudChangeStatus, setCloudChangeStatus] = useState<{[key: string]: 'success' | 'error'}>({});

  // Code fixes state management
  const [currentCodeFixes, setCurrentCodeFixes] = useState<Record<string, CodeFix[]> | null>(null);
  const [codeChangeStatus, setCodeChangeStatus] = useState<{[key: string]: 'success' | 'error' | 'loading'}>({});
  const [codeProvider, setCodeProvider] = useState<string | null>(null);

  // Changelog toast notification
  const [showChangelogToast, setShowChangelogToast] = useState(false);
  const changelogToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up changelog toast timer on unmount
  useEffect(() => {
    return () => {
      if (changelogToastTimerRef.current) {
        clearTimeout(changelogToastTimerRef.current);
        changelogToastTimerRef.current = null;
      }
    };
  }, []);

  // Cluster and cloud connection autocomplete states
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [awsConnections, setAwsConnections] = useState<AWSConnection[]>([]);
  const [ociConnections, setOciConnections] = useState<OCIConnection[]>([]);
  // Inventory context for dynamic sample prompts
  const [promptInventory, setPromptInventory] = useState<InventoryContext | undefined>();
  const [flowInventory, setFlowInventory] = useState<InventoryContext | undefined>();
  const [ddInventory, setDdInventory] = useState<InventoryContext | undefined>();
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteQuery, setAutocompleteQuery] = useState('');
  const [autocompleteType, setAutocompleteType] = useState<'cluster' | 'cloud'>('cluster');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStartIndex, setMentionStartIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);

  // Welcome screen category tab
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);

  // Chat history states
  const [showChatHistory, setShowChatHistory] = useState(false);

  // Auto-scroll: keep scrolled to bottom during streaming UNLESS user has scrolled up.
  // Checks position directly at the time of each update — no event listeners needed.
  const lastScrollHeightRef = useRef(0);

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;

    const newHeight = el.scrollHeight;
    if (newHeight === lastScrollHeightRef.current) return;

    // Check if we're currently near the bottom BEFORE this content was added.
    // The content just grew by (newHeight - lastScrollHeightRef.current) pixels.
    // If we were within 150px of the OLD bottom, we should follow.
    const growth = newHeight - lastScrollHeightRef.current;
    const distFromOldBottom = (lastScrollHeightRef.current) - el.scrollTop - el.clientHeight;

    lastScrollHeightRef.current = newHeight;

    // Only auto-scroll if the user was near the bottom of the PREVIOUS content
    if (distFromOldBottom < 150) {
      el.scrollTop = newHeight;
    }
  }, [messages, currentBotMessage, isThinking, thinkingText]);

  useEffect(() => {
    let isComponentMounted = true;

    // Only initialize WebSocket if user is authenticated
    if (!isAuthenticated || authLoading) {
      return;
    }

    // Initialize WebSocket connection
    const initializeWebSocket = async () => {
      try {
        setConnectionError(null);
        await webSocketClient.connect();
        if (isComponentMounted) {
          setIsConnected(true);
        }
      } catch (error) {
        if (isComponentMounted) {
          console.error('Failed to connect to WebSocket:', error);
          // Only show error after initial connection attempts
          setTimeout(() => {
            if (isComponentMounted && !hasConnectedBefore) {
              setConnectionError('Failed to connect to chat service. Please try refreshing the page.');
              setIsConnected(false);
              setShowConnectionStatus(true);
            }
          }, 2000);
        }
      }
    };

    // Set up WebSocket event listeners
    webSocketClient.onConnect(() => {
      if (isComponentMounted) {
        setIsConnected(true);
        setConnectionError(null);
        setHasConnectedBefore(true);
        // NOTE: Do NOT set isInitialConnection to false here!
        // The useEffect that clears the session needs isInitialConnection=true to run.
        // It will be set to false after the session is cleared.
        // Delay showing connection status to prevent flash
        setTimeout(() => {
          if (isComponentMounted) {
            setShowConnectionStatus(true);
          }
        }, 1000);
      }
    });

    // Add global message handler for session notifications
    const handleGlobalMessage = (message: any) => {
      if (message.type === 'current_session') {
        const sessionId = message.content?.session_id;
        if (sessionId && sessionId !== currentSessionId) {
          setCurrentSessionId(sessionId);
          
          // Refresh the chat history in the sidebar immediately since the session is now saved to PostgreSQL
          // The backend only sends this notification after the session is fully persisted
          refreshSessions();
        }
      }
    };

    webSocketClient.onMessage(handleGlobalMessage);

    webSocketClient.onDisconnect(() => {
      if (isComponentMounted) {
        setIsConnected(false);
        // Only show disconnected status if we were previously connected
        if (hasConnectedBefore) {
          setShowConnectionStatus(true);
          setConnectionError('Connection lost. Attempting to reconnect...');
        }
      }
    });

    webSocketClient.onError((error) => {
      if (isComponentMounted) {
        console.error('WebSocket error:', error);
        // Only show error if not during initial connection attempt
        if (!isInitialConnection && hasConnectedBefore) {
          setConnectionError('Connection error occurred.');
          setShowConnectionStatus(true);
        }
      }
    });

    // Start connection
    initializeWebSocket();

    // Cleanup on unmount
    return () => {
      isComponentMounted = false;
      webSocketClient.offMessage(handleGlobalMessage);
      webSocketClient.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading]);

  // Fetch clusters and cloud connections when user is authenticated, poll every 15s
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;

    const fetchTargets = async () => {
      try {
        const [clustersResponse, awsResponse, ociResponse] = await Promise.all([
          apiClient.listClusters(),
          apiClient.getAWSConnectionStatus('cloud-ingestion').catch(() => ({ connections: [], count: 0 })),
          apiClient.getOCIConnectionStatus().catch(() => ({ connections: [], count: 0 }))
        ]);
        setClusters(clustersResponse.clusters || []);
        setAwsConnections(awsResponse.connections || []);
        setOciConnections(ociResponse.connections || []);
      } catch (error) {
        console.error('Failed to fetch targets:', error);
      }
    };

    fetchTargets();
    const interval = setInterval(fetchTargets, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated, authLoading]);

  // Fetch inventory sample data once for dynamic sample prompts
  // Runs once when clusters are available, picks the first connected cluster
  useEffect(() => {
    if (!isAuthenticated || authLoading || promptInventory || clusters.length === 0) return;

    const firstConnected = clusters.find(c => c.is_connected);
    if (!firstConnected) return;

    // Fetch namespaces (and optionally workloads) for a cluster's inventory context.
    // invClusters is passed in to avoid redundant API calls when fetching multiple clusters.
    const fetchInventoryForCluster = async (
      clusterName: string,
      invClusters: { name: string; uid: string }[],
      options?: { skipWorkloads?: boolean },
    ): Promise<InventoryContext | undefined> => {
      const invCluster = invClusters.find(c => c.name === clusterName);
      if (!invCluster?.uid) return undefined;

      const nsResponse = await apiClient.getInventoryNamespaces(invCluster.uid);
      const allNamespaces = nsResponse.namespaces || [];

      // Filter to user/application namespaces and shuffle for variety on each page load
      const systemPrefixes = ['kube-', 'autonp', 'gke-', 'istio-', 'cert-manager', 'ingress-', 'kestrel-'];
      const systemExact = new Set(['kube-system', 'default', 'elasticsearch', 'observability', 'node-pressure-sim']);
      const userNsList = allNamespaces.filter(
        ns => !systemPrefixes.some(p => ns.name.startsWith(p)) && !systemExact.has(ns.name)
      );
      for (let i = userNsList.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [userNsList[i], userNsList[j]] = [userNsList[j], userNsList[i]];
      }

      if (options?.skipWorkloads) {
        return { namespaces: userNsList, workloads: [] };
      }

      // Fetch workloads from up to 3 shuffled namespaces, tagging each with its namespace name
      const nsSlice = userNsList.slice(0, 3);
      const workloadResults = await Promise.all(
        nsSlice.map(ns =>
          apiClient.getInventoryWorkloads(ns.id)
            .then(r => (r.workloads || []).map(w => ({ ...w, namespaceName: ns.name })))
            .catch(() => [] as WorkloadWithNamespace[])
        )
      );
      const workloads: WorkloadWithNamespace[] = workloadResults.flat();
      for (let i = workloads.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [workloads[i], workloads[j]] = [workloads[j], workloads[i]];
      }

      return { namespaces: userNsList, workloads };
    };

    const fetchInventorySample = async () => {
      try {
        // Fetch cluster list once and reuse for all inventory lookups
        const invClustersResp = await apiClient.getInventoryClusters();
        const invClusters = invClustersResp.clusters || [];

        const inv = await fetchInventoryForCluster(firstConnected.cluster_name, invClusters);
        if (inv) setPromptInventory(inv);

        // If a different cluster has active flows, fetch its namespace inventory for Network prompts
        const firstFlowCluster = clusters.find(c => c.is_connected && c.has_active_flows);
        if (firstFlowCluster && firstFlowCluster.cluster_name !== firstConnected.cluster_name) {
          const flowInv = await fetchInventoryForCluster(firstFlowCluster.cluster_name, invClusters, { skipWorkloads: true });
          if (flowInv) setFlowInventory(flowInv);
        } else if (firstFlowCluster) {
          if (inv) setFlowInventory(inv);
        }

        // If a different cluster has Datadog, fetch its workload inventory for Metrics prompts
        const firstDdCluster = clusters.find(c => c.is_connected && c.has_datadog);
        if (firstDdCluster && firstDdCluster.cluster_name !== firstConnected.cluster_name) {
          const ddInv = await fetchInventoryForCluster(firstDdCluster.cluster_name, invClusters);
          if (ddInv) setDdInventory(ddInv);
        } else if (firstDdCluster) {
          if (inv) setDdInventory(inv);
        }
      } catch (error) {
        // Non-critical — fall back to generic prompts
        console.debug('Failed to fetch inventory for prompts:', error);
      }
    };

    fetchInventorySample();
  }, [isAuthenticated, authLoading, clusters, promptInventory]);

  // Clear chat session when component first mounts (on page load/refresh)
  // This ensures Redis is cleared and the agent doesn't see stale chat history
  // IMPORTANT: Only clear on initial connection, NOT on reconnections
  useEffect(() => {
    let hasCleared = false;

    const clearSession = async () => {
      // Only run once when WebSocket is connected for the FIRST TIME (not on reconnections)
      if (isConnected && !hasCleared && isAuthenticated && isInitialConnection) {
        hasCleared = true;
        console.log('Clearing chat session on initial page load');

        // Wait a brief moment for WebSocket to stabilize
        await new Promise(resolve => setTimeout(resolve, 100));

        // Call handleNewChat to clear the session via WebSocket
        handleNewChat();

        // Mark initial connection as complete so future reconnects don't clear the session
        setIsInitialConnection(false);
      }
    };

    clearSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, isAuthenticated, isInitialConnection]);

  // Save current session when component unmounts or user navigates away
  useEffect(() => {
    const handleBeforeUnload = () => {
      // This will be handled by the backend automatically when the WebSocket disconnects
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Render a single @mention badge (cluster or cloud)
  const renderMentionBadge = (fullMatch: string, isCloud: boolean, targetName: string, key: string) => {
    if (isCloud) {
      const isOciConnection = targetName === 'oci' || targetName.toLowerCase().startsWith('oci-');
      const connection = awsConnections.find(c => c.account_id === targetName);
      return (
        <span
          key={key}
          className={cn(
            "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium transition-colors",
            "shadow-sm ring-1 ring-inset align-baseline",
            "relative top-[1px]",
            "bg-zinc-100 text-zinc-900 ring-zinc-300/50 dark:bg-zinc-900 dark:text-zinc-100 dark:ring-zinc-700/50"
          )}
          title={isOciConnection ? `OCI: ${targetName}` : (connection ? `AWS: ${connection.account_id} - ${connection.connection_status}` : `Cloud: ${targetName}`)}
        >
          <Cloud className="h-3 w-3 flex-shrink-0" />
          <span className="leading-none">@cloud:{targetName}</span>
        </span>
      );
    } else {
      const cluster = clusters.find(c => c.cluster_name === targetName);
      return (
        <span
          key={key}
          className={cn(
            "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium transition-colors",
            "shadow-sm ring-1 ring-inset align-baseline",
            "relative top-[-1px]",
            "bg-zinc-100 text-zinc-900 ring-zinc-300/50 dark:bg-zinc-900 dark:text-zinc-100 dark:ring-zinc-700/50"
          )}
          title={cluster ? `${cluster.cluster_name} - ${cluster.is_connected ? 'Connected' : 'Disconnected'}` : 'Unknown cluster'}
        >
          <div className="w-1.5 h-1.5 rounded-full shadow-sm flex-shrink-0 bg-zinc-400 dark:bg-zinc-600" />
          <span className="leading-none">@{targetName}</span>
        </span>
      );
    }
  };

  // Parse inline text and replace @mentions with styled badges
  const renderTextWithMentions = (text: string) => {
    const mentionRegex = /@(cloud:)?([a-zA-Z0-9_-]+)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      let mentionStart = match.index;
      let mentionEnd = mentionStart + match[0].length;
      let isCloud = match[1] === 'cloud:';
      let targetName = match[2];

      let textAfterMatch = text.substring(mentionEnd);
      if (/^\.[a-zA-Z]/.test(textAfterMatch)) continue;

      if (!isCloud && targetName.toLowerCase() === 'cloud') {
        const spaceOciMatch = textAfterMatch.match(/^ (oci-[a-zA-Z0-9_-]+)/);
        if (spaceOciMatch) {
          isCloud = true;
          targetName = spaceOciMatch[1];
          mentionEnd += spaceOciMatch[0].length;
        }
      }

      if (mentionStart > lastIndex) {
        parts.push(text.substring(lastIndex, mentionStart));
      }

      parts.push(renderMentionBadge(match[0], isCloud, targetName, `mention-${mentionStart}`));
      lastIndex = mentionEnd;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? <>{parts}</> : <>{text}</>;
  };

  // Render message with full markdown support + @mention highlighting
  const renderMessageWithMentions = (text: string) => {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Paragraphs: render inline mentions
          p: ({ children }) => {
            const processed = React.Children.map(children, child => {
              if (typeof child === 'string') return renderTextWithMentions(child);
              return child;
            });
            return <p className="mb-2 last:mb-0">{processed}</p>;
          },
          // Bold
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          // Italic
          em: ({ children }) => <em className="italic">{children}</em>,
          // Code blocks
          code: ({ className, children, ...props }) => {
            const isBlock = className?.startsWith('language-');
            if (isBlock) {
              return (
                <div className="my-2 rounded-md overflow-hidden border border-border/50">
                  <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50 border-b border-border/50">
                    <span className="text-[10px] font-mono text-muted-foreground uppercase">{className?.replace('language-', '') || 'code'}</span>
                    <button
                      onClick={() => navigator.clipboard.writeText(String(children).replace(/\n$/, ''))}
                      className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                  <pre className="p-3 overflow-x-auto bg-muted/30">
                    <code className="text-xs font-mono">{children}</code>
                  </pre>
                </div>
              );
            }
            return (
              <code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono" {...props}>
                {children}
              </code>
            );
          },
          // Pre: just pass through (code block handles styling)
          pre: ({ children }) => <>{children}</>,
          // Lists
          ul: ({ children }) => <ul className="mb-2 ml-4 list-disc space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal space-y-1">{children}</ol>,
          li: ({ children }) => {
            const processed = React.Children.map(children, child => {
              if (typeof child === 'string') return renderTextWithMentions(child);
              return child;
            });
            return <li>{processed}</li>;
          },
          // Headers
          h1: ({ children }) => <h1 className="text-lg font-bold mb-2 mt-3">{children}</h1>,
          h2: ({ children }) => <h2 className="text-base font-bold mb-2 mt-3">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sm font-bold mb-1 mt-2">{children}</h3>,
          h4: ({ children }) => <h4 className="text-sm font-semibold mb-1 mt-2">{children}</h4>,
          // Horizontal rule
          hr: () => <hr className="my-3 border-border/50" />,
          // Links
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">
              {children}
            </a>
          ),
          // Tables
          table: ({ children }) => (
            <div className="my-2 overflow-x-auto">
              <table className="min-w-full text-xs border-collapse border border-border/50">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-muted/50">{children}</thead>,
          th: ({ children }) => <th className="px-2 py-1 text-left font-semibold border border-border/50">{children}</th>,
          td: ({ children }) => <td className="px-2 py-1 border border-border/50">{children}</td>,
          // Blockquotes
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-primary/30 pl-3 my-2 text-muted-foreground italic">{children}</blockquote>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    );
  };

  // Filter clusters and AWS connections based on autocomplete query and type
  const filteredClusters = clusters.filter(cluster =>
    cluster.cluster_name.toLowerCase().includes(autocompleteQuery.toLowerCase())
  );

  // Detect mentioned clusters whose control stream is down
  const controlStreamWarningCluster = useMemo(() => {
    return clusters.find(c =>
      input.includes(`@${c.cluster_name}`) && c.stream_health?.control === false
    );
  }, [input, clusters]);

  // Create "All AWS Accounts" option that queries all connected accounts
  const allAwsOption: AWSConnection = {
    id: 'all-aws',
    tenant_id: '',
    account_id: 'aws', // Use 'aws' as the identifier for "all accounts"
    role_arn: '',
    external_id: '',
    region: '',
    connection_status: awsConnections.some(c => c.connection_status === 'connected') ? 'connected' : 'disconnected',
    created_at: '',
    updated_at: '',
  };

  // Create "All OCI" option that queries all connected OCI tenancies
  const allOciOption: AWSConnection = {
    id: 'all-oci',
    tenant_id: '',
    account_id: 'oci', // Use 'oci' as the identifier for "all OCI tenancies"
    role_arn: '',
    external_id: '',
    region: '',
    connection_status: ociConnections.some(c => c.connection_status === 'connected') ? 'connected' : 'disconnected',
    created_at: '',
    updated_at: '',
  };

  // Convert OCI connections to AWSConnection format for unified autocomplete
  const ociConnectionsAsAws: AWSConnection[] = ociConnections
    .filter(c => c.connection_status === 'connected')
    .map(c => ({
      id: `oci-${c.id}`,
      tenant_id: c.tenant_id,
      account_id: `oci-${(c.connection_name || c.tenancy_ocid.split('.').pop()?.substring(0, 8) || 'unknown').replace(/\s+/g, '-')}`,
      role_arn: '',
      external_id: '',
      region: c.region,
      connection_status: c.connection_status,
      created_at: c.created_at,
      updated_at: c.updated_at,
    }));

  // Filter AWS connections and prepend "All AWS" option when there are connections
  const hasConnectedAws = awsConnections.some(c => c.connection_status === 'connected');
  const hasConnectedOci = ociConnections.some(c => c.connection_status === 'connected');
  
  const filteredAwsConnectionsRaw = awsConnections.filter(connection =>
    connection.account_id.toLowerCase().includes(autocompleteQuery.toLowerCase())
  );

  // Filter OCI connections based on query
  const filteredOciConnectionsRaw = ociConnectionsAsAws.filter(connection =>
    connection.account_id.toLowerCase().includes(autocompleteQuery.toLowerCase())
  );

  // Include "All AWS Accounts" option at the top when:
  // - There are connected AWS accounts AND
  // - Query matches "aws" or is empty
  const showAllAwsOption = hasConnectedAws &&
    (autocompleteQuery === '' || 'aws'.includes(autocompleteQuery.toLowerCase()));

  // Include "All OCI" option when:
  // - There are connected OCI accounts AND
  // - Query matches "oci" or is empty
  const showAllOciOption = hasConnectedOci &&
    (autocompleteQuery === '' || 'oci'.includes(autocompleteQuery.toLowerCase()));

  // Combine AWS and OCI connections for autocomplete
  const filteredAwsConnections = [
    ...(showAllAwsOption ? [allAwsOption] : []),
    ...filteredAwsConnectionsRaw.filter(c => c.id !== 'all-aws'),
    ...(showAllOciOption ? [allOciOption] : []),
    ...filteredOciConnectionsRaw.filter(c => c.id !== 'all-oci'),
  ];

  // Show cloud suggestions when:
  // 1. In cloud mode: show filtered AWS connections
  // 2. Query is empty: show first 3 AWS connections
  // 3. Query matches the start of "cloud:" (e.g., 'c', 'cl', 'clo', 'cloud', 'cloud:')
  const cloudQueryPrefix = 'cloud:';
  const isTypingCloudMention = autocompleteQuery !== '' &&
    cloudQueryPrefix.toLowerCase().startsWith(autocompleteQuery.toLowerCase());

  // When typing toward @cloud:, show unfiltered cloud connections (with "All AWS" and "All OCI" options)
  const unFilteredCloudWithAllOptions = [
    ...(hasConnectedAws ? [allAwsOption] : []),
    ...awsConnections.filter(c => c.id !== 'all-aws'),
    ...(hasConnectedOci ? [allOciOption] : []),
    ...ociConnectionsAsAws.filter(c => c.id !== 'all-oci'),
  ];

  const cloudAutocompleteItems = autocompleteType === 'cloud'
    ? filteredAwsConnections
    : (autocompleteQuery === ''
        ? unFilteredCloudWithAllOptions.slice(0, 4)
        : (isTypingCloudMention ? unFilteredCloudWithAllOptions.slice(0, 4) : []));

  // Handle input changes and detect @ mentions
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    setCursorPosition(cursorPos);
    setInput(value);

    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'; // 120px = max-h-30

    // Find the last @ before cursor position
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Check if there's a space between @ and cursor (which would end the mention)
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      const hasSpaceAfterAt = textAfterAt.includes(' ');

      if (!hasSpaceAfterAt) {
        // Check if this is a cloud mention (@cloud:)
        const isCloudMention = textAfterAt.startsWith('cloud:');

        if (isCloudMention) {
          // Cloud mention - extract query after "cloud:"
          const cloudQuery = textAfterAt.substring(6); // Remove "cloud:"
          setAutocompleteType('cloud');
          setAutocompleteQuery(cloudQuery);
          setMentionStartIndex(lastAtIndex);
          setShowAutocomplete(true);
          setSelectedIndex(0);
        } else {
          // Regular cluster mention
          setAutocompleteType('cluster');
          setAutocompleteQuery(textAfterAt);
          setMentionStartIndex(lastAtIndex);
          setShowAutocomplete(true);
          setSelectedIndex(0);
        }
      } else {
        setShowAutocomplete(false);
      }
    } else {
      setShowAutocomplete(false);
    }
  };

  // Insert selected cluster mention
  const insertClusterMention = (cluster: Cluster) => {
    const beforeMention = input.substring(0, mentionStartIndex);
    const afterCursor = input.substring(cursorPosition);
    const newValue = `${beforeMention}@${cluster.cluster_name} ${afterCursor}`;

    setInput(newValue);
    setShowAutocomplete(false);
    setAutocompleteQuery('');

    // Focus back to input and position cursor after the mention
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = mentionStartIndex + cluster.cluster_name.length + 2; // +2 for @ and space
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        // Trigger auto-resize
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
      }
    }, 0);
  };

  // Insert selected cloud (AWS) mention
  // Uses account_id as the mention identifier (e.g., @cloud:aws for all accounts, @cloud:123456789012 for specific)
  const insertCloudMention = (connection: AWSConnection) => {
    const beforeMention = input.substring(0, mentionStartIndex);
    const afterCursor = input.substring(cursorPosition);
    const newValue = `${beforeMention}@cloud:${connection.account_id} ${afterCursor}`;

    setInput(newValue);
    setShowAutocomplete(false);
    setAutocompleteQuery('');
    setAutocompleteType('cluster');

    // Focus back to input and position cursor after the mention
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = mentionStartIndex + connection.account_id.length + 8; // +8 for "@cloud:" and space
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        // Trigger auto-resize
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
      }
    }, 0);
  };

  const handleSamplePrompt = (prompt: string) => {
    setInput(prompt);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        // Trigger auto-resize for the sample prompt
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
      }
    }, 50);
  };

  // Chat history handlers
  const handleSessionSelect = async (sessionId: string) => {
    try {
      // Load the selected session and get its messages
      const sessionMessages = await loadSession(sessionId);
      
      // Convert session messages to the current Message format
      const convertedMessages: Message[] = sessionMessages.map(msg => ({
        id: msg.id || `${msg.role}-${Date.now()}`,
        text: msg.content,
        sender: msg.role === 'user' ? 'user' as const : 'bot' as const,
        timestamp: new Date(msg.created_at || Date.now()).getTime(),
        thinking: msg.thinking_text ? {
          text: msg.thinking_text,
          steps: [], // Steps are not persisted in DB, fallback parsing will be used
          isExpanded: false,
        } : undefined,
        yamlFixes: msg.yaml_fixes ? msg.yaml_fixes as Record<string, YAMLFix[]> : undefined,
        cloudFixes: msg.cloud_fixes ? msg.cloud_fixes as Record<string, CloudFix[]> : undefined,
        datadogMetrics: msg.datadog_metrics ? msg.datadog_metrics as DatadogMetricResult[] : undefined,
        codeFixes: msg.code_fixes ? (() => {
          // Extract code_fixes but remove the _provider metadata key
          const fixes = { ...msg.code_fixes } as Record<string, any>;
          delete fixes._provider;
          return Object.keys(fixes).length > 0 ? fixes as Record<string, CodeFix[]> : undefined;
        })() : undefined,
        // Restore code_provider: first try msg.code_provider (live session), then _provider embedded in code_fixes JSONB (restored session)
        codeProvider: msg.code_provider || (msg.code_fixes as any)?._provider || undefined,
      }));

      setMessages(convertedMessages);
      setCurrentBotMessage('');
      setCurrentYAMLFixes(null);
      setCurrentCloudFixes(null);
      setCurrentCodeFixes(null);
      setCodeChangeStatus({});
      setCodeProvider(null);

      // Restore codeProvider from the last message with code fixes
      const lastCodeFixMsg = convertedMessages.slice().reverse().find(m => m.codeFixes);
      if (lastCodeFixMsg?.codeProvider) {
        setCodeProvider(lastCodeFixMsg.codeProvider);
      }

      // Restore lastMessageIdRef and lastUserQueryRef from the last user message
      // so "Store Change" on historical code fixes gets the correct chat_message_id
      const lastUserMsg = convertedMessages.slice().reverse().find(m => m.sender === 'user');
      if (lastUserMsg) {
        lastUserQueryRef.current = lastUserMsg.text || '';
        lastMessageIdRef.current = lastUserMsg.id || '';
      }

    } catch (error) {
      console.error('Failed to load chat session:', error);
    }
  };

  const handleNewChat = async () => {
    if (!isConnected) return;

    // Generate unique ID for this request
    const newChatId = `new-chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Set up one-time response handler for new_chat_response or error
    const responseHandler = (message: any) => {
      if (message.id === newChatId) {
        if (message.type === 'new_chat_response') {
          // Success - clear the UI
          setMessages([]);
          setCurrentBotMessage('');
          setCurrentYAMLFixes(null);
          setCurrentCloudFixes(null);
          setCurrentCodeFixes(null);
          setCodeProvider(null);
          setCodeChangeStatus({});
          setCurrentSessionId(null);
          lastUserQueryRef.current = '';
          lastMessageIdRef.current = '';
        } else if (message.type === 'error') {
          // Failure - show error to user
          console.error('Failed to start new chat:', message.content);
          // Optionally show an error message to the user
          setMessages((prev) => [...prev, {
            id: `error-${Date.now()}`,
            text: `Failed to start new chat: ${message.content}`,
            sender: 'bot',
            timestamp: Date.now(),
          }]);
        }

        // Remove this handler after processing
        webSocketClient.offMessage(responseHandler);
      }
    };

    // Register handler before sending message
    webSocketClient.onMessage(responseHandler);

    try {
      // Send new_chat message with the ID
      webSocketClient.sendMessage({
        type: 'new_chat',
        content: {},
        id: newChatId,
      });
    } catch (error) {
      console.error('Failed to send new chat request:', error);
      webSocketClient.offMessage(responseHandler);
    }
  };

  const handleCancel = () => {
    if (currentMessageId && canCancel) {
      // Send cancel message
      cancelChatMessage(currentMessageId);

      // Mark as cancelled so the handler ignores any stale messages
      cancelledRef.current = true;

      // Remove the active handler to stop processing stale responses
      if (activeHandlerRef.current) {
        webSocketClient.offMessage(activeHandlerRef.current);
        activeHandlerRef.current = null;
      }

      // Clear the stale-response timeout so it doesn't fire later
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
        messageTimeoutRef.current = null;
      }

      // Preserve whatever has been streamed so far (like ChatGPT does)
      const partialText = currentBotMessage;
      const partialYAMLFixes = currentYAMLFixes;
      const partialCloudFixes = currentCloudFixes;
      const partialThinking = thinkingText;
      const partialSteps = thinkingSteps;

      // Reset streaming states
      setIsLoading(false);
      setIsTyping(false);
      setIsThinking(false);
      setThinkingComplete(false);
      setThinkingText('');
      setThinkingSteps([]);
      setCurrentBotMessage('');
      setCurrentYAMLFixes(null);
      setCurrentCloudFixes(null);
      setCurrentCodeFixes(null);
      if (waitingForFixesTimerRef.current) clearTimeout(waitingForFixesTimerRef.current);
      setWaitingForFixes(false);
      setCanCancel(false);
      setCurrentMessageId(null);
      
      // Preserve partial text if any, otherwise show a cancellation notice
      setMessages((prev) => [...prev, {
        id: `cancelled-${Date.now()}`,
        text: partialText || 'Request cancelled.',
        sender: 'bot',
        timestamp: Date.now(),
        thinking: partialThinking ? {
          text: partialThinking,
          steps: partialSteps,
          isExpanded: false,
        } : undefined,
        yamlFixes: partialYAMLFixes || undefined,
        cloudFixes: partialCloudFixes || undefined,
      }]);
    }
  };

  const showChangelogNotification = () => {
    // Clear any existing timer so repeated clicks reset the duration
    if (changelogToastTimerRef.current) {
      clearTimeout(changelogToastTimerRef.current);
    }
    setShowChangelogToast(true);
    changelogToastTimerRef.current = setTimeout(() => {
      setShowChangelogToast(false);
      changelogToastTimerRef.current = null;
    }, 4000);
  };

  const handleStoreYAMLChange = async (clusterName: string, fix: YAMLFix) => {
    const fixKey = `${clusterName}-${fix.file_path}`;
    
    try {
      // Find cluster ID from cluster name
      const cluster = clusters.find(c => c.cluster_name === clusterName);
      if (!cluster) {
        console.error('Cluster not found:', clusterName);
        setYamlChangeStatus(prev => ({ ...prev, [fixKey]: 'error' }));
        return;
      }

      const request = {
        cluster_id: cluster.id,
        file_path: fix.file_path,
        file_type: fix.file_type,
        yaml_content: fix.yaml_content,
        existing_yaml: fix.existing_yaml || '',
        explanation: fix.explanation || '',
        resource_name: fix.resource_name || '',
        resource_kind: fix.resource_kind || '',
        resource_namespace: fix.resource_namespace || '',
        chat_message_id: lastMessageIdRef.current || '',
        user_query: lastUserQueryRef.current || '',
      };

      await apiClient.createChatYAMLChange(request);

      // Show success status
      setYamlChangeStatus(prev => ({ ...prev, [fixKey]: 'success' }));
      showChangelogNotification();
      
      // Clear status after 3 seconds
      setTimeout(() => {
        setYamlChangeStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[fixKey];
          return newStatus;
        });
      }, 3000);
      
    } catch (error: any) {
      console.error('Failed to store YAML change:', error);
      setYamlChangeStatus(prev => ({ ...prev, [fixKey]: 'error' }));

      // Clear error status after 5 seconds
      setTimeout(() => {
        setYamlChangeStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[fixKey];
          return newStatus;
        });
      }, 5000);
    }
  };

  const handleStoreCloudChange = async (connectionName: string, fix: CloudFix) => {
    const fixKey = `${connectionName}-${fix.resource_type}-${fix.resource_name}`;

    try {
      // Determine if this is an OCI or AWS connection based on fix_type or connection name
      const isOCIFix = fix.fix_type === 'oci_cli' || connectionName.startsWith('oci-') || connectionName.toLowerCase() === 'oci';
      
      let request: Parameters<typeof apiClient.createChatCloudChange>[0];
      
      if (isOCIFix) {
        // Handle OCI connection
        // Try to find OCI connection ID from connection name
        const ociConnection = ociConnections.find(c => {
          // Match by connection name (with or without oci- prefix, hyphens for spaces)
          const normalizedConnectionName = connectionName.replace('oci-', '').replace(/-/g, ' ');
          return c.connection_name?.toLowerCase() === normalizedConnectionName.toLowerCase() ||
                 c.connection_name?.toLowerCase().replace(/\s+/g, '-') === connectionName.replace('oci-', '').toLowerCase();
        });

        request = {
          oci_connection_id: ociConnection?.id,
          oci_connection_name: connectionName,
          change_type: fix.fix_type, // terraform, cloudformation, pulumi, oci_cli
          resource_type: fix.resource_type,
          resource_name: fix.resource_name,
          action: fix.action,
          content: fix.content,
          existing_content: fix.existing_content || '',
          explanation: fix.explanation || '',
          file_path: fix.file_path || '',
          chat_message_id: lastMessageIdRef.current || '',
        };
      } else {
        // Handle AWS connection
        // Try to find AWS connection ID from connection name (could be account_id or fix category)
        const awsConnection = awsConnections.find(c => c.account_id === connectionName);

        request = {
          aws_connection_id: awsConnection?.id,
          aws_account_id: connectionName,
          change_type: fix.fix_type, // terraform, cloudformation, aws_cli
          resource_type: fix.resource_type,
          resource_name: fix.resource_name,
          action: fix.action,
          content: fix.content,
          existing_content: fix.existing_content || '',
          explanation: fix.explanation || '',
          file_path: fix.file_path || '',
          chat_message_id: lastMessageIdRef.current || '',
        };
      }

      // Call the API to store the cloud change
      await apiClient.createChatCloudChange(request);

      // Show success status - keep it persistent so user knows it's stored
      setCloudChangeStatus(prev => ({ ...prev, [fixKey]: 'success' }));
      showChangelogNotification();

      // Don't clear success status - keep button as "Stored" to indicate change was saved

    } catch (error: any) {
      console.error('Failed to store cloud change:', error);
      setCloudChangeStatus(prev => ({ ...prev, [fixKey]: 'error' }));

      // Clear error status after 5 seconds
      setTimeout(() => {
        setCloudChangeStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[fixKey];
          return newStatus;
        });
      }, 5000);
    }
  };

  const handleStoreCodeChange = async (repoName: string, fixes: CodeFix[]) => {
    const repoStatusKey = `repo-${repoName}`;

    try {
      setCodeChangeStatus(prev => ({ ...prev, [repoStatusKey]: 'loading' }));

      // Try codeProvider state first, then check the message that contains these fixes, then default to 'github'
      let platform = codeProvider || 'github';
      if (!codeProvider) {
        // When session is restored from history, codeProvider state is null.
        // Try to find the platform from the message that has these code fixes.
        const fixMsg = messages.find(m => m.codeFixes && m.codeFixes[repoName]);
        if (fixMsg?.codeProvider) {
          platform = fixMsg.codeProvider;
        }
      }

      // Store all fixes for this repo, tracking partial success
      let stored = 0;
      let lastError: any = null;
      for (const fix of fixes) {
        try {
          await apiClient.createChatCodeChange({
            repo_platform: platform,
            repo_full_name: repoName,
            github_repository_id: platform === 'github' && fix.repo_id ? fix.repo_id : undefined,
            gitlab_repository_id: platform === 'gitlab' && fix.repo_id ? fix.repo_id : undefined,
            file_path: fix.file_path,
            line_start: fix.line_range[0],
            line_end: fix.line_range[1],
            language: fix.language || '',
            original_code: fix.original_code,
            fixed_code: fix.fixed_code,
            explanation: fix.explanation || '',
            chat_message_id: lastMessageIdRef.current || '',
            user_query: lastUserQueryRef.current || '',
          });
          stored++;
        } catch (err) {
          lastError = err;
          console.error(`Failed to store code change for ${fix.file_path}:`, err);
        }
      }

      if (stored === fixes.length) {
        setCodeChangeStatus(prev => ({ ...prev, [repoStatusKey]: 'success' }));
        showChangelogNotification();
      } else if (stored > 0) {
        // Partial success — some fixes stored, some failed
        console.warn(`Stored ${stored}/${fixes.length} code changes for ${repoName}`);
        setCodeChangeStatus(prev => ({ ...prev, [repoStatusKey]: 'success' }));
        showChangelogNotification();
      } else {
        throw lastError || new Error('Failed to store code changes');
      }

    } catch (error: any) {
      console.error('Failed to store code changes:', error);
      setCodeChangeStatus(prev => ({ ...prev, [repoStatusKey]: 'error' }));

      setTimeout(() => {
        setCodeChangeStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[repoStatusKey];
          return newStatus;
        });
      }, 5000);
    }
  };

  const handleSend = async () => {
    if (input.trim() === '' || !isConnected || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: input.trim(),
      sender: 'user',
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const userInput = input.trim();
    setInput('');
    setIsLoading(true);
    setIsTyping(false); // Start with no typing since we might be thinking first
    setCurrentBotMessage('');
    
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = '44px'; // Reset to min height
    }
    
    // Reset thinking state for new message and immediately show thinking UI
    setThinkingText('');
    setThinkingSteps([]);
    setThinkingComplete(false);
    setIsThinkingExpanded(false);
    setIsThinking(true);  // Show thinking UI immediately

    // Focus back to input for better UX
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);

    // Set up streaming response handler variables
    let accumulatedMessage = '';
    let accumulatedThinking = '';
    let accumulatedSteps: ThinkingStep[] = [];
    let receivedYAMLFixes: Record<string, YAMLFix[]> | null = null;
    let receivedCloudFixes: Record<string, CloudFix[]> | null = null;
    let receivedDatadogMetrics: DatadogMetricResult[] | null = null;
    let receivedCodeFixes: Record<string, CodeFix[]> | null = null;
    let receivedCodeProvider: string | undefined = undefined;

    try {
      // Send message via WebSocket and track the message ID for cancellation
      const messageId = sendChatMessage(userInput);
      setCurrentMessageId(messageId);
      setCanCancel(true);
      cancelledRef.current = false;

      // Persist query and message ID for "Store Change" clicks (survives state clears in onComplete)
      lastUserQueryRef.current = userInput;
      lastMessageIdRef.current = messageId;
      
      // Create bot message placeholder
      const botMessageId = `bot-${Date.now()}`;
      let botMessageComplete = false;

      const cleanupHandler = () => {
        if (activeHandlerRef.current) {
          webSocketClient.offMessage(activeHandlerRef.current);
          activeHandlerRef.current = null;
        }
      };
      
      // Set up streaming response handler with thinking support and YAML fixes
      const handleResponse = handleStreamingResponse(
        messageId,
        // onChunk - handle answer chunks
        (chunk: string) => {
          if (cancelledRef.current) return;
          accumulatedMessage += chunk;
          setCurrentBotMessage(accumulatedMessage);
          setIsTyping(false);
        },
        // onComplete - answer complete
        () => {
          if (cancelledRef.current) { cleanupHandler(); return; }
          if (waitingForFixesTimerRef.current) clearTimeout(waitingForFixesTimerRef.current);
          setWaitingForFixes(false);
          setMessages((prev) => [...prev, {
            id: botMessageId,
            text: accumulatedMessage,
            sender: 'bot',
            timestamp: Date.now(),
            thinking: accumulatedThinking ? {
              text: accumulatedThinking,
              steps: accumulatedSteps,
              isExpanded: isThinkingExpanded,
            } : undefined,
            yamlFixes: receivedYAMLFixes || undefined,
            cloudFixes: receivedCloudFixes || undefined,
            datadogMetrics: receivedDatadogMetrics || undefined,
            codeFixes: receivedCodeFixes || undefined,
            codeProvider: receivedCodeProvider || codeProvider || undefined,
          }]);
          setCurrentBotMessage('');
          setCurrentYAMLFixes(null);
          setCurrentCloudFixes(null);
          setCurrentDatadogMetrics(null);
          setCurrentCodeFixes(null);
          setIsLoading(false);
          setIsTyping(false);
          setIsThinking(false);
          setThinkingComplete(false);
          setThinkingText('');
          setThinkingSteps([]);
          setCanCancel(false);
          setCurrentMessageId(null);
          botMessageComplete = true;
          cleanupHandler();
        },
        // onThinking - handle thinking chunks with optional structured steps
        (thinkingChunk: string, steps?: ThinkingStep[]) => {
          if (cancelledRef.current) return;
          accumulatedThinking += thinkingChunk;
          setThinkingText(accumulatedThinking);
          // Accumulate structured steps if provided, de-duplicating by step id
          if (steps && steps.length > 0) {
            const existingIds = new Set(accumulatedSteps.map(s => s.id));
            const newSteps = steps.filter(s => !existingIds.has(s.id));
            if (newSteps.length > 0) {
              accumulatedSteps = [...accumulatedSteps, ...newSteps];
              setThinkingSteps(accumulatedSteps);
            }
          }
          setIsThinking(true);
          setIsTyping(false);
        },
        // onThinkingComplete - thinking phase complete, start answer
        () => {
          setThinkingComplete(true);
          setIsTyping(true);
        },
        // onCancelled - server acknowledged cancellation; handleCancel already
        // preserved partial text and reset UI, so just clean up the handler.
        () => {
          cleanupHandler();
          botMessageComplete = true;
        },
        // onYAMLFixes - handle YAML fixes
        (fixes: Record<string, YAMLFix[]>) => {
          if (cancelledRef.current) return;
          receivedYAMLFixes = fixes;
          setCurrentYAMLFixes(fixes);
          if (waitingForFixesTimerRef.current) clearTimeout(waitingForFixesTimerRef.current);
          setWaitingForFixes(false);
        },
        // onError - handle error messages
        (errorMessage: string) => {
          cleanupHandler();
          if (cancelledRef.current) return;
          setMessages((prev) => [...prev, {
            id: botMessageId,
            text: errorMessage,
            sender: 'bot',
            timestamp: Date.now(),
            thinking: accumulatedThinking ? {
              text: accumulatedThinking,
              steps: accumulatedSteps,
              isExpanded: isThinkingExpanded,
            } : undefined,
          }]);
          setCurrentBotMessage('');
          setCurrentYAMLFixes(null);
          setCurrentCloudFixes(null);
          setCurrentCodeFixes(null);
          setIsLoading(false);
          setIsTyping(false);
          setIsThinking(false);
          setThinkingComplete(false);
          setThinkingText('');
          setThinkingSteps([]);
          setCanCancel(false);
          setCurrentMessageId(null);
          botMessageComplete = true;
        },
        // onCloudFixes - handle Cloud fixes (Terraform, CloudFormation, AWS CLI)
        (fixes: Record<string, CloudFix[]>) => {
          if (cancelledRef.current) return;
          receivedCloudFixes = fixes;
          setCurrentCloudFixes(fixes);
          if (waitingForFixesTimerRef.current) clearTimeout(waitingForFixesTimerRef.current);
          setWaitingForFixes(false);
        },
        // onTextComplete - text streaming is done, fixes may be loading.
        // Delay slightly before showing indicator to avoid a flash when no fixes exist
        // (done: true arrives almost immediately in that case).
        () => {
          const timer = setTimeout(() => setWaitingForFixes(true), 300);
          waitingForFixesTimerRef.current = timer;
        },
        // onDatadogMetrics - handle inline chart data
        (metrics: any[]) => {
          if (cancelledRef.current) return;
          receivedDatadogMetrics = metrics as DatadogMetricResult[];
          setCurrentDatadogMetrics(metrics as DatadogMetricResult[]);
        },
        // onCodeFixes - handle source code fixes from connected repos
        (fixes: Record<string, CodeFix[]>, provider?: string) => {
          if (cancelledRef.current) return;
          receivedCodeFixes = fixes;
          receivedCodeProvider = provider;
          setCurrentCodeFixes(fixes);
          if (provider) setCodeProvider(provider);
          if (waitingForFixesTimerRef.current) clearTimeout(waitingForFixesTimerRef.current);
          setWaitingForFixes(false);
        }
      );

      // Listen for WebSocket messages and store ref so handleCancel can remove it
      activeHandlerRef.current = handleResponse;
      webSocketClient.onMessage(handleResponse);

      // Set a timeout in case the response never comes
      messageTimeoutRef.current = setTimeout(() => {
        if (!botMessageComplete && !cancelledRef.current) {
          cleanupHandler();
          setMessages((prev) => [...prev, {
            id: botMessageId,
            text: 'Sorry, I encountered an error processing your request. Please try again.',
            sender: 'bot',
            timestamp: Date.now(),
            thinking: accumulatedThinking ? {
              text: accumulatedThinking,
              steps: accumulatedSteps,
              isExpanded: isThinkingExpanded,
            } : undefined,
            yamlFixes: receivedYAMLFixes || undefined,
            cloudFixes: receivedCloudFixes || undefined,
            datadogMetrics: receivedDatadogMetrics || undefined,
            codeFixes: receivedCodeFixes || undefined,
            codeProvider: receivedCodeProvider || codeProvider || undefined,
          }]);
          setCurrentBotMessage('');
          setCurrentYAMLFixes(null);
          setCurrentCloudFixes(null);
          setCurrentDatadogMetrics(null);
          setCurrentCodeFixes(null);
          setIsLoading(false);
          setIsTyping(false);
          setCanCancel(false);
          setCurrentMessageId(null);
        }
      }, 300000); // 5 mins timeout (matches backend context timeout for large IaC queries)

    } catch (error) {
      console.error('Failed to send message:', error);
      if (activeHandlerRef.current) {
        webSocketClient.offMessage(activeHandlerRef.current);
        activeHandlerRef.current = null;
      }
      setMessages((prev) => [...prev, {
        id: `error-${Date.now()}`,
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: Date.now(),
        thinking: accumulatedThinking ? {
          text: accumulatedThinking,
          steps: accumulatedSteps,
          isExpanded: isThinkingExpanded,
        } : undefined,
        yamlFixes: receivedYAMLFixes || undefined,
        cloudFixes: receivedCloudFixes || undefined,
        datadogMetrics: receivedDatadogMetrics || undefined,
        codeFixes: receivedCodeFixes || undefined,
        codeProvider: receivedCodeProvider || codeProvider || undefined,
      }]);
      setIsLoading(false);
      setIsTyping(false);
      setCurrentYAMLFixes(null);
      setCurrentCloudFixes(null);
      setCurrentDatadogMetrics(null);
      setCurrentCodeFixes(null);
      setCanCancel(false);
      setCurrentMessageId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Calculate total autocomplete items (clusters first, then cloud)
    const totalItems = autocompleteType === 'cloud'
      ? filteredAwsConnections.length
      : filteredClusters.length + cloudAutocompleteItems.length;

    if (showAutocomplete && totalItems > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < totalItems - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev > 0 ? prev - 1 : totalItems - 1
          );
          break;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          if (autocompleteType === 'cloud') {
            // Cloud-only mode
            if (selectedIndex < filteredAwsConnections.length) {
              insertCloudMention(filteredAwsConnections[selectedIndex]);
            }
          } else {
            // Mixed mode: clusters first, then cloud
            if (selectedIndex < filteredClusters.length) {
              insertClusterMention(filteredClusters[selectedIndex]);
            } else {
              const cloudIndex = selectedIndex - filteredClusters.length;
              if (cloudIndex < cloudAutocompleteItems.length) {
                insertCloudMention(cloudAutocompleteItems[cloudIndex]);
              }
            }
          }
          break;
        case 'Escape':
          e.preventDefault();
          setShowAutocomplete(false);
          break;
        default:
          // For other keys, let the normal input handling continue
          break;
      }
    } else if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift+Enter: Allow new line (default textarea behavior)
        return;
      } else {
        // Enter: Send message
        e.preventDefault();
        handleSend();
      }
    }
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-background to-muted/30">
      {/* Chat History Sidebar */}
      {isMobile ? (
        <Sheet open={showChatHistory} onOpenChange={setShowChatHistory}>
          <SheetContent side="left" className="p-0 w-80">
            <ChatHistorySidebar
              currentSessionId={currentSessionId || undefined}
              onSessionSelect={(id) => { handleSessionSelect(id); setShowChatHistory(false); }}
              onNewChat={() => { handleNewChat(); setShowChatHistory(false); }}
              isDrawer
              sessions={sessions}
              loading={chatHistoryLoading}
              deleteSession={deleteSession}
              restoreSession={restoreSession}
              updateSessionTitle={updateSessionTitle}
            />
          </SheetContent>
        </Sheet>
      ) : (
        showChatHistory && (
          <ChatHistorySidebar
            currentSessionId={currentSessionId || undefined}
            onSessionSelect={handleSessionSelect}
            onNewChat={handleNewChat}
            className="flex-shrink-0"
            sessions={sessions}
            loading={chatHistoryLoading}
            deleteSession={deleteSession}
            restoreSession={restoreSession}
            updateSessionTitle={updateSessionTitle}
          />
        )
      )}
      
      {/* Main Chat Area */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Modern Header with Glass Effect */}
        <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MobileMenuButton />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowChatHistory(!showChatHistory)}
              className="p-2"
              title="Toggle chat history"
              aria-label={showChatHistory ? "Hide chat history" : "Show chat history"}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div>
              <h2 className="text-2xl font-bold text-foreground">
                Cloud AI Copilot
              </h2>
              <div className="flex items-center gap-2 mt-0.5">
                {showConnectionStatus && (
                  <>
                    {isConnected ? (
                      <>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-xs text-muted-foreground dark:text-green-400 font-medium">
                          Connected
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                          Disconnected
                        </span>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          <ModeToggle />
        </div>
      </div>

      {/* Messages Area with Fixed Height and Scrolling */}
      <div 
        ref={scrollAreaRef} 
        className="flex-1 overflow-y-auto px-4 py-6 space-y-6"
        style={{ 
          height: 'calc(100 * var(--vh) - 160px)',
          minHeight: '400px'
        }}
      >
        {connectionError && (
          <div className="animate-in slide-in-from-top-2 duration-300 mx-auto max-w-2xl">
            <div className="flex items-center gap-3 p-4 bg-red-50/80 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl backdrop-blur-sm">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-300 font-medium">{connectionError}</p>
            </div>
          </div>
        )}
        
        {messages.length === 0 && !isLoading && !connectionError ? (
          <div className="flex flex-col items-center text-center animate-in fade-in-50 duration-700 pt-20">
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Welcome to your Cloud AI Copilot
            </h3>
            <p className="text-muted-foreground text-sm max-w-md mb-5 leading-relaxed">
              Ask me anything about your cloud infrastructure. Use <code className="bg-muted px-1 py-0.5 rounded text-xs">@cluster-name</code> for Kubernetes clusters and <code className="bg-muted px-1 py-0.5 rounded text-xs">@cloud:connection</code> for Cloud.
            </p>

            {(() => {
              const categories = getPromptCategories(clusters, awsConnections, ociConnections, promptInventory, flowInventory, ddInventory);
              const selected = categories[selectedCategoryIndex] || categories[0];
              const SelectedIcon = selected.icon;

              return (
                <div className="max-w-xl w-full animate-in slide-in-from-bottom-2 duration-500">
                  {/* Category tabs */}
                  <div className="flex items-center justify-center gap-1.5 mb-4 flex-wrap">
                    {categories.map((category, i) => {
                      const Icon = category.icon;
                      const isActive = i === selectedCategoryIndex;
                      return (
                        <button
                          key={category.title}
                          onClick={() => setSelectedCategoryIndex(i)}
                          className={cn(
                            "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium",
                            "transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
                            isActive
                              ? "bg-foreground text-background"
                              : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <Icon className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">{category.title}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Prompts for selected category */}
                  <div className="space-y-2 animate-in fade-in duration-300" key={selectedCategoryIndex}>
                    <p className="text-xs text-muted-foreground mb-3">
                      {selected.description}
                    </p>
                    {selected.prompts.map((prompt) => (
                      <button
                        key={prompt}
                        onClick={() => handleSamplePrompt(prompt)}
                        disabled={!isConnected}
                        className={cn(
                          "group w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm",
                          "bg-card/60 backdrop-blur-sm border border-border/40",
                          "hover:bg-card hover:border-border/80 hover:shadow-md",
                          "transition-all duration-200",
                          "disabled:opacity-50 disabled:cursor-not-allowed"
                        )}
                      >
                        <div className={cn(
                          "flex-shrink-0 p-1.5 rounded-lg transition-colors duration-200",
                          "bg-muted/50 group-hover:bg-muted"
                        )}>
                          <SelectedIcon className="h-4 w-4 text-primary/70 group-hover:text-primary transition-colors duration-200" />
                        </div>
                        <span className="flex-1 text-muted-foreground group-hover:text-foreground transition-colors duration-200 leading-relaxed">
                          {prompt}
                        </span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground/30 group-hover:text-primary/50 group-hover:translate-x-0.5 transition-all duration-200 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-6 flex flex-col">
            {/* Historical messages - older messages that get pushed up and out of view */}
            {(() => {
              // If we're loading, exclude the last user message (it's the active conversation)
              // If not loading, include all messages
              const completedMessages = isLoading && messages.length > 0 && messages[messages.length - 1].sender === 'user' 
                ? messages.slice(0, -1) 
                : messages;
              
              // Display all completed messages in chronological order (oldest first, newest last)
              return completedMessages.map((message, index) => {
                if (message.sender === 'user') {
                  return (
                    <div key={message.id} className={cn(
                      "animate-in slide-in-from-bottom-2 duration-500 flex gap-4 justify-end"
                    )}
                    style={{ animationDelay: `${index * 50}ms` }}>
                      <div className="max-w-[80%] ml-12">
                        <div className="rounded-2xl px-4 py-3 shadow-sm bg-foreground text-background">
                          <div className="text-sm leading-relaxed break-words prose-chat">
                            {renderMessageWithMentions(message.text)}
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted border border-border/50 flex items-center justify-center">
                        <User className="h-4 w-4 text-foreground" />
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div key={message.id} className={cn(
                      "animate-in slide-in-from-bottom-2 duration-500 flex gap-4 justify-start"
                    )}
                    style={{ animationDelay: `${index * 50 + 25}ms` }}>
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted border border-border/50 flex items-center justify-center">
                        <Bot className="h-4 w-4 text-primary" />
                      </div>
                      <div className="max-w-[80%] space-y-2">
                        {/* Thinking Display for Bot Messages */}
                        {message.thinking && (
                          <ThinkingDisplay 
                            thinking={message.thinking}
                            messageId={message.id}
                            clusters={clusters}
                            awsConnections={awsConnections}
                          />
                        )}
                        
                        {/* Message Bubble */}
                        <div className="rounded-2xl px-4 py-3 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                          <div className="text-sm leading-relaxed break-words prose-chat">
                            {renderMessageWithMentions(message.text)}
                          </div>
                          
                          {/* Datadog Metrics Charts */}
                          {message.datadogMetrics && message.datadogMetrics.length > 0 && (
                            <DatadogMetricsChart metrics={message.datadogMetrics} />
                          )}

                          {/* YAML Fixes Display */}
                          {message.yamlFixes && (
                            <YAMLFixesDisplay
                              yamlFixes={message.yamlFixes}
                              clusters={clusters}
                              onStoreChange={handleStoreYAMLChange}
                              yamlChangeStatus={yamlChangeStatus}
                            />
                          )}

                          {/* Cloud Fixes Display */}
                          {message.cloudFixes && (
                            <CloudFixesDisplay
                              cloudFixes={message.cloudFixes}
                              awsConnections={awsConnections}
                              ociConnections={ociConnections}
                              onStoreChange={handleStoreCloudChange}
                              cloudChangeStatus={cloudChangeStatus}
                            />
                          )}

                          {/* Code Fixes Display */}
                          {message.codeFixes && (
                            <CodeFixesDisplay
                              codeFixes={message.codeFixes}
                              onStoreChange={handleStoreCodeChange}
                              codeChangeStatus={codeChangeStatus}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
              });
            })()}

            {/* Active conversation at the bottom - always visible */}
            {isLoading && (
              <div className="space-y-4">
                {/* Show the most recent user message if we're processing */}
                {messages.length > 0 && messages[messages.length - 1].sender === 'user' && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-4 justify-end">
                    <div className="max-w-[80%] ml-12">
                      <div className="rounded-2xl px-4 py-3 shadow-sm bg-foreground text-background">
                        <div className="text-sm leading-relaxed break-words prose-chat">
                          {renderMessageWithMentions(messages[messages.length - 1].text)}
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted border border-border/50 flex items-center justify-center">
                      <User className="h-4 w-4 text-foreground" />
                    </div>
                  </div>
                )}

                {/* Thinking Indicator - Show below user message */}
                {isThinking && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-4 justify-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted border border-border/50 flex items-center justify-center relative overflow-hidden">
                      <Brain className="h-4 w-4 text-muted-foreground relative z-10 animate-pulse" style={{animationDuration: '2.5s'}} />
                    </div>
                    <ThinkingDisplay
                      thinking={{ text: thinkingText, steps: thinkingSteps, isExpanded: isThinkingExpanded }}
                      messageId="streaming"
                      clusters={clusters}
                      awsConnections={awsConnections}
                      isStreaming={!thinkingComplete}
                    />
                  </div>
                )}

                {/* Typing Indicator - Show below thinking */}
                {isTyping && !currentBotMessage && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-4 justify-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted border border-border/50 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                    <div className="rounded-2xl px-4 py-3 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                      <div className="flex items-center space-x-1">
                        <span className="h-2 w-2 bg-muted-foreground/60 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                        <span className="h-2 w-2 bg-muted-foreground/60 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                        <span className="h-2 w-2 bg-muted-foreground/60 rounded-full animate-bounce"></span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Streaming Bot Message - Show below user message */}
                {currentBotMessage && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-4 justify-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted border border-border/50 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                    <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                      <div className="text-sm leading-relaxed break-words prose-chat">
                        {renderMessageWithMentions(currentBotMessage)}
                      </div>
                      
                      {/* Show current Datadog metrics charts if available */}
                      {currentDatadogMetrics && currentDatadogMetrics.length > 0 && (
                        <DatadogMetricsChart metrics={currentDatadogMetrics} />
                      )}

                      {/* Show current YAML fixes if available */}
                      {currentYAMLFixes && (
                        <YAMLFixesDisplay
                          yamlFixes={currentYAMLFixes}
                          clusters={clusters}
                          onStoreChange={handleStoreYAMLChange}
                          yamlChangeStatus={yamlChangeStatus}
                        />
                      )}

                      {/* Show current Cloud fixes if available */}
                      {currentCloudFixes && (
                        <CloudFixesDisplay
                          cloudFixes={currentCloudFixes}
                          awsConnections={awsConnections}
                          ociConnections={ociConnections}
                          onStoreChange={handleStoreCloudChange}
                          cloudChangeStatus={cloudChangeStatus}
                        />
                      )}

                      {/* Show current Code fixes if available */}
                      {currentCodeFixes && (
                        <CodeFixesDisplay
                          codeFixes={currentCodeFixes}
                          onStoreChange={handleStoreCodeChange}
                          codeChangeStatus={codeChangeStatus}
                        />
                      )}

                      {waitingForFixes && (
                        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-border/30">
                          <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin" />
                          <span className="text-xs text-muted-foreground">Loading configuration fixes</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modern Input Area */}
      <div className="sticky bottom-0 bg-background/80 backdrop-blur-lg border-t border-border/50 p-4">
        <div className="max-w-4xl mx-auto">
          {controlStreamWarningCluster && (
            <div className="flex items-center gap-2 px-3 py-2 mb-2 bg-amber-50 dark:bg-amber-950/40 border border-border/50 rounded-lg text-xs text-muted-foreground">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-foreground" />
              <span>Chat functionality is limited — the control stream for <strong>{controlStreamWarningCluster.cluster_name}</strong> is currently disconnected.</span>
            </div>
          )}
          <div className="relative flex items-end gap-3 p-2 bg-card/50 backdrop-blur-sm border border-border/50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
            {/* Unified Autocomplete Dropdown - Clusters and Cloud */}
            {showAutocomplete && (autocompleteType === 'cloud' ? filteredAwsConnections.length > 0 : (filteredClusters.length > 0 || cloudAutocompleteItems.length > 0)) && (
              <div className="absolute bottom-full left-2 right-2 mb-2 bg-background/95 backdrop-blur-sm border border-border rounded-xl shadow-xl max-h-64 overflow-y-auto z-50 ring-1 ring-black/5 dark:ring-white/10">
                {/* Kubernetes Clusters Section */}
                {autocompleteType !== 'cloud' && filteredClusters.length > 0 && (
                  <>
                    <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/30 border-b border-border/50 flex items-center gap-2">
                      <Server className="h-3 w-3" />
                      Kubernetes Clusters
                    </div>
                    {filteredClusters.map((cluster, index) => (
                      <button
                        key={cluster.id}
                        className={cn(
                          "w-full text-left px-4 py-3 text-sm transition-all duration-150",
                          "flex items-center gap-3 border-b border-border/50",
                          "hover:bg-muted/50 focus:bg-muted/50 focus:outline-none",
                          index === selectedIndex && "bg-muted/80 shadow-sm"
                        )}
                        onClick={() => insertClusterMention(cluster)}
                        onMouseEnter={() => setSelectedIndex(index)}
                      >
                        {/* Chat requires the control stream — if control is down, show as disconnected
                            regardless of overall connection_status */}
                        {(() => {
                          const controlUp = cluster.stream_health?.control !== false;
                          return (
                            <>
                              <div className={cn(
                                "w-2 h-2 rounded-full flex-shrink-0 shadow-sm",
                                controlUp
                                  ? "bg-emerald-500 shadow-emerald-500/20"
                                  : "bg-red-500 shadow-red-500/20"
                              )} />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-foreground truncate">
                                  {cluster.cluster_name}
                                </div>
                                {cluster.description && (
                                  <div className="text-xs text-muted-foreground/80 truncate mt-0.5">
                                    {cluster.description}
                                  </div>
                                )}
                              </div>
                              <div className={cn(
                                "text-xs font-medium",
                                controlUp
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : "text-red-600 dark:text-red-400"
                              )}>
                                {controlUp ? 'Connected' : 'Disconnected'}
                              </div>
                            </>
                          );
                        })()}
                      </button>
                    ))}
                  </>
                )}

                {/* Cloud Connections Section (AWS & OCI) */}
                {(autocompleteType === 'cloud' ? filteredAwsConnections : cloudAutocompleteItems).length > 0 && (
                  <>
                    <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/30 border-b border-border/50 flex items-center gap-2">
                      <Cloud className="h-3 w-3 text-muted-foreground" />
                      <span>Cloud Accounts</span>
                      {autocompleteType !== 'cloud' && (
                        <span className="text-muted-foreground/60 font-normal">— type @cloud: for more</span>
                      )}
                    </div>
                    {(autocompleteType === 'cloud' ? filteredAwsConnections : cloudAutocompleteItems).map((connection, index) => {
                      const actualIndex = autocompleteType === 'cloud' ? index : filteredClusters.length + index;
                      const isAllAwsOption = connection.id === 'all-aws';
                      const isAllOciOption = connection.id === 'all-oci';
                      const isOciConnection = connection.id.startsWith('oci-') || isAllOciOption;
                      const isSpecialOption = isAllAwsOption || isAllOciOption;
                      
                      // Get display name for the connection
                      const getDisplayName = () => {
                        if (isAllAwsOption) return 'All AWS Accounts';
                        if (isAllOciOption) return 'All OCI Tenancies';
                        return connection.account_id;
                      };
                      
                      // Get description text
                      const getDescription = () => {
                        if (isAllAwsOption) {
                          return `Query all ${awsConnections.filter(c => c.connection_status === 'connected').length} connected AWS accounts`;
                        }
                        if (isAllOciOption) {
                          return `Query all ${ociConnections.filter(c => c.connection_status === 'connected').length} connected OCI tenancies`;
                        }
                        if (isOciConnection) {
                          return `OCI Tenancy • ${connection.region || 'default region'}`;
                        }
                        return `AWS Account: ${connection.account_id} • ${connection.region}`;
                      };
                      
                      return (
                        <button
                          key={connection.id}
                          className={cn(
                            "w-full text-left px-4 py-3 text-sm transition-all duration-150",
                            "flex items-center gap-3 border-b border-border/50 last:border-b-0",
                            isOciConnection
                              ? "hover:bg-muted/50 focus:bg-muted/50"
                              : "hover:bg-muted/50 focus:bg-muted/50",
                            "focus:outline-none",
                            actualIndex === selectedIndex && (isOciConnection 
                              ? "bg-muted/80 shadow-sm"
                              : "bg-muted/80 shadow-sm"),
                            isAllAwsOption && "bg-muted/30",
                            isAllOciOption && "bg-muted/30"
                          )}
                          onClick={() => insertCloudMention(connection)}
                          onMouseEnter={() => setSelectedIndex(actualIndex)}
                        >
                          {isOciConnection ? (
                            <img src={OCILogo} alt="OCI" className="h-4 w-4 flex-shrink-0" />
                          ) : (
                            <>
                              <img src={awsLogoLight} alt="AWS" className="h-4 w-4 flex-shrink-0 dark:hidden" />
                              <img src={awsLogoDark} alt="AWS" className="h-4 w-4 flex-shrink-0 hidden dark:block" />
                            </>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-foreground truncate">
                              {getDisplayName()}
                            </div>
                            <div className="text-xs text-muted-foreground/80 truncate mt-0.5">
                              {getDescription()}
                            </div>
                          </div>
                          <div className={cn(
                            "text-xs font-medium",
                            isSpecialOption
                              ? (isOciConnection ? "text-red-600 dark:text-red-400" : "text-orange-600 dark:text-orange-400")
                              : connection.connection_status === 'connected'
                                ? (isOciConnection ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400")
                                : "text-red-600 dark:text-red-400"
                          )}>
                            {isSpecialOption ? 'Recommended' : connection.connection_status === 'connected' ? 'Connected' : 'Disconnected'}
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            )}
            
            <div className="flex-1 min-h-[44px] max-h-30">
              <textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={isConnected ? "Message Cloud AI... Use @ for clusters, @cloud: for AWS/OCI" : "Connecting..."}
                className={cn(
                  "w-full border-0 bg-transparent resize-none text-sm leading-relaxed",
                  "focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none placeholder:text-muted-foreground/60",
                  "min-h-[44px] max-h-30 py-3 px-4 overflow-y-auto",
                  "font-sans"
                )}
                disabled={isLoading || !isConnected}
                style={{ 
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgb(156 163 175 / 0.3) transparent'
                }}
              />
            </div>
            
            <div className="flex-shrink-0 self-center">
              <Button
                onClick={canCancel ? handleCancel : handleSend}
                disabled={(!input.trim() && !canCancel) || !isConnected}
                size="icon"
                className={cn(
                  "h-11 w-11 rounded-xl transition-all duration-200",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  canCancel 
                    ? "bg-foreground hover:bg-foreground/90 text-background" 
                    : "bg-primary hover:bg-primary/90",
                  (!input.trim() && !canCancel) || (!isConnected && !canCancel)
                    ? "scale-95 opacity-50" 
                    : "scale-100 hover:scale-105 shadow-lg"
                )}
                title={canCancel ? "Stop request" : "Send message"}
              >
                {canCancel ? (
                  <X className="h-4 w-4" />
                ) : isLoading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          
          <p className="text-xs text-center text-muted-foreground/80 mt-3 px-4">
            AI can make mistakes. Please verify important information about your cloud environment.
          </p>
        </div>
      </div>
      </div>

      {/* Changelog toast notification */}
      <div
        role="status"
        aria-live="polite"
        className={cn(
          "fixed bottom-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ease-out",
          showChangelogToast
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-4 pointer-events-none"
        )}
      >
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-green-600 text-white shadow-lg text-sm">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          <span>Change added to</span>
          <Link
            to="/chat-changelog"
            className="font-medium underline underline-offset-2 hover:text-green-100 inline-flex items-center gap-1"
          >
            Changelog
            <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}